import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import * as XLSX from 'xlsx'
import { useAuth } from '../context/AuthContext'
import { useProjectAuth } from './useProjectAuth'

// Same audit_trail insert shape already used in IPC.jsx / PurchaseOrders.jsx /
// Subcontracts.jsx (identical table/columns in all three — this file
// follows the same established pattern for the write itself). The
// SOURCE of actor_name is deliberately different, scoped to this file
// only: those three modules pass a role selected from a localStorage
// dropdown (tempRole), not a real signed-in identity — see
// PurchaseOrders.jsx's `useState(() => localStorage.getItem('temp_role')...)`.
// CostCodes.jsx instead uses AuthContext's real authenticated profile
// (profile.full_name, falling back to profile.email) — AuthContext is a
// fully real Supabase Auth system (user_profiles, invited_users) that
// already existed but wasn't being consumed by any audit-writing module
// yet. This change is intentionally scoped to CostCodes.jsx only, per
// explicit decision — PurchaseOrders/IPC/Subcontracts are NOT touched
// here and keep using tempRole; migrating them to AuthContext is a
// separate, later refactor.
// previous_status/new_status are used here only where the changed value
// genuinely IS a short classification string (cost_type:
// 'direct'/'indirect') — for Reparent, where the changed value is a
// parent_id (uuid) rather than a status, the before/after codes are put
// in `comment` instead, since previous_status/new_status are shaped for
// short status-like values elsewhere in this project, not arbitrary
// identifiers.
async function logAudit(entityId, entityType, action, actorName, comment, prevStatus, newStatus, projectId) {
  try {
    await supabase.from('audit_trail').insert({
      entity_id: entityId, entity_type: entityType, action,
      actor_name: actorName, comment, previous_status: prevStatus,
      new_status: newStatus, project_id: projectId,
    })
  } catch(e) { console.error('audit log failed', e) }
}

export default function CostCodes({ initProject }) {
  const { profile } = useAuth()
  // actor_name for every audit_trail row written from this file — the
  // real signed-in user's display name, falling back to their email if
  // full_name isn't set on their profile, falling back to null (never a
  // placeholder string) if neither is available — an audit row with a
  // null actor is honest about missing data; a row saying "System" or
  // "Unknown" would not be.
  const actorName = profile?.full_name || profile?.email || null

  const [activeTab, setActiveTab] = useState('tree') // 'tree' | 'dictionary'
  const [codes, setCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editCode, setEditCode] = useState(null)
  const [expandedParents, setExpandedParents] = useState(new Set())
  const [expandedResources, setExpandedResources] = useState(new Set())
  const [showUpload, setShowUpload] = useState(false)
  const [uploadRows, setUploadRows] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const fileRef = useRef()

  // ── Resource Dictionary state ─────────────────────────────
  const [resourceDict, setResourceDict] = useState([])

  // ── Indirect Categories state ──────────────────────────────
  const [categories, setCategories] = useState([])

  // ── Tender match-check: compares Resource Codes in the tree vs. what's actually in the tender ──
  const [tenderResourceMap, setTenderResourceMap] = useState({}) // cost_code (normalized) -> Set of resource_code (normalized)
  const [showMismatchDetail, setShowMismatchDetail] = useState(null) // { costCode, missingHere, missingThere }

  // ── Dependency-aware Operations: Unified Operation Lifecycle state ──
  // pendingOperation holds everything the Impact Analysis modal needs to
  // show, and everything the eventual Execute step needs to run, for
  // whichever Reparent/Cost Type change is currently awaiting user
  // confirmation. null = no operation pending (modal hidden).
  //   { operation: 'reparent'|'costType', codeLabel: string,
  //     usage: {total, modules} | null, usageLoading: boolean,
  //     execute: () => Promise<void> }
  const [pendingOperation, setPendingOperation] = useState(null)

  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [showCategoryList, setShowCategoryList] = useState(false)
  const [editCategory, setEditCategory] = useState(null)
  const [categoryNameInput, setCategoryNameInput] = useState('')
  const [categorySelectedCC, setCategorySelectedCC] = useState(new Set())
  const [dictLoading, setDictLoading] = useState(true)
  const [showDictModal, setShowDictModal] = useState(false)
  const [editDictItem, setEditDictItem] = useState(null)
  const [dictSearch, setDictSearch] = useState('')
  const [dictFilterType, setDictFilterType] = useState('all')
  const [showDictUpload, setShowDictUpload] = useState(false)
  const [dictUploadRows, setDictUploadRows] = useState([])
  const [dictUploading, setDictUploading] = useState(false)
  const [dictUploadError, setDictUploadError] = useState('')
  const dictFileRef = useRef()
  const [dictForm, setDictForm] = useState({
    code: '', resource_type: 'material', description: '', unit: '', is_active: true
  })

  // Picker: which cost_code parent are we adding a Resource Code under
  const [resourcePicker, setResourcePicker] = useState(null) // { parentId } | null
  const [resourcePickerSearch, setResourcePickerSearch] = useState('')
  // Picker for linking a fully-orphaned Sub Code (parent_id NULL) directly to a Cost Code (level 1)
  const [orphanSubCostCodePicker, setOrphanSubCostCodePicker] = useState(null) // { subId } | null
  const [orphanSubPickerSearch, setOrphanSubPickerSearch] = useState('')

  const [projectId, setProjectId] = useState(null)
  // Real authorization source -- can() mirrors has_permission() exactly.
  const { can } = useProjectAuth(projectId)

  // ── Validation (data integrity check across modules) ──────
  const [validationResults, setValidationResults] = useState(null) // null = not run yet
  const [validationLoading, setValidationLoading] = useState(false)
  const [validationError, setValidationError] = useState('')

  // ── Audit Trail viewer — read-only display of audit_trail rows this
  // file itself writes (entity_type='cost_code', via logAudit() above,
  // for Reparent and Cost Type changes). Loaded lazily when the tab is
  // opened, not on every page load, since it's a secondary/reference view.
  const [auditEntries, setAuditEntries] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditLoaded, setAuditLoaded] = useState(false)
  const [auditError, setAuditError] = useState('')

  useEffect(() => { if (initProject?.id) setProjectId(initProject.id) }, [initProject])

  const [form, setForm] = useState({
    code: '', description: '', unit: '', parent_id: '',
    level: '2', cost_type: 'direct', is_active: true
  })

  useEffect(() => { if (projectId) { fetchCodes(); fetchResourceDict(); fetchCategories(); fetchTenderResourceMap() } }, [projectId])

  async function fetchCodes(pid, opts = {}) {
    const { silent = false } = opts
    if (!silent) setLoading(true)
    const pid2 = pid || projectId
    let query = supabase.from('cost_codes').select('*').order('code')
    if (pid2) query = query.eq('project_id', pid2)
    const { data } = await query
    const list = data || []
    setCodes(list)
    if (!silent) {
      // Expand all Cost Codes by default — only on first load, not on every refresh
      const parentIds = list.filter(c => parseInt(c.level) === 1).map(c => c.id)
      setExpandedParents(new Set(parentIds))
    }
    setLoading(false)
  }

  // ── Indirect Categories: fetch, save, delete ───────────────
  async function fetchCategories() {
    let query = supabase.from('indirect_categories').select('*').order('sort_order', { ascending: true, nullsFirst: false }).order('name')
    if (projectId) query = query.eq('project_id', projectId)
    const { data } = await query
    setCategories(data || [])
  }

  function openNewCategory() {
    setEditCategory(null)
    setCategoryNameInput('')
    setCategorySelectedCC(new Set())
    setShowCategoryModal(true)
  }

  function openEditCategory(cat) {
    setEditCategory(cat)
    setCategoryNameInput(cat.name)
    const memberIds = new Set(codes.filter(c => c.indirect_category_id === cat.id).map(c => c.id))
    setCategorySelectedCC(memberIds)
    setShowCategoryModal(true)
  }

  async function saveCategory() {
    const name = categoryNameInput.trim()
    if (!name) { alert('اكتب اسم الكاتيجوري'); return }
    let categoryId = editCategory?.id
    if (editCategory) {
      const { error } = await supabase.from('indirect_categories').update({ name }).eq('id', editCategory.id)
      if (error) { alert(error.message); return }
    } else {
      const maxSort = categories.reduce((m, c) => Math.max(m, c.sort_order || 0), 0)
      const { data, error } = await supabase.from('indirect_categories')
        .insert({ project_id: projectId, name, sort_order: maxSort + 1 }).select().single()
      if (error) { alert(error.message); return }
      categoryId = data.id
    }
    // Update cost_codes.indirect_category_id: assign selected ones to this category,
    // unassign any previously-assigned ones that got unchecked
    const previousMembers = editCategory ? codes.filter(c => c.indirect_category_id === categoryId).map(c => c.id) : []
    const toAssign = Array.from(categorySelectedCC)
    const toUnassign = previousMembers.filter(id => !categorySelectedCC.has(id))
    if (toAssign.length) await supabase.from('cost_codes').update({ indirect_category_id: categoryId }).in('id', toAssign)
    if (toUnassign.length) await supabase.from('cost_codes').update({ indirect_category_id: null }).in('id', toUnassign)
    setShowCategoryModal(false)
    await Promise.all([fetchCategories(), fetchCodes(null, { silent: true })])
  }

  async function deleteCategory(catId) {
    if (!window.confirm('حذف الكاتيجوري؟ الـ Cost Codes هترجع بدون تصنيف (مش هتتمسح).')) return
    await supabase.from('cost_codes').update({ indirect_category_id: null }).eq('indirect_category_id', catId)
    await supabase.from('indirect_categories').delete().eq('id', catId)
    await Promise.all([fetchCategories(), fetchCodes(null, { silent: true })])
  }

  // ── Tender Match-Check: build a normalized map of what Resource Codes actually
  // exist in the tender for each Cost Code, so the tree can flag mismatches with
  // what's manually configured in the Cost Codes hierarchy.
  const norm = (s) => String(s || '').trim().toUpperCase()
  // Shared plain-data reader for MODULE_SOURCES' idFields/textFields: a
  // field descriptor's `field` is either a single column name (string)
  // or a priority-ordered list of column names (array) — this reads the
  // first non-empty one. It is NOT a transformation (it never combines,
  // derives, or reshapes values) — it only picks which existing column's
  // raw value to use, per the priority the metadata itself declares.
  // idFields never currently use the array form (no id column needs a
  // fallback today), but the helper accepts both shapes uniformly so
  // that remains a metadata decision, not a code-path decision.
  const readPriorityField = (row, field) => {
    const names = Array.isArray(field) ? field : [field]
    for (const name of names) {
      if (row[name]) return row[name]
    }
    return null
  }

  async function fetchTenderResourceMap() {
    if (!projectId) return
    const { data } = await supabase
      .from('tender_direct_items')
      .select('cost_code, resource_code, row_type')
      .eq('project_id', projectId)
      .in('row_type', ['material','labour','subcontract','equipment'])
    const map = {}
    for (const r of (data || [])) {
      if (!r.cost_code || !r.resource_code) continue
      const cc = norm(r.cost_code)
      const rc = norm(r.resource_code)
      if (!map[cc]) map[cc] = new Set()
      map[cc].add(rc)
    }
    setTenderResourceMap(map)
  }

  // ── Dependency-aware Operations: shared source-of-truth METADATA ──────
  // MODULE_SOURCES is a declarative registry — it describes DATA, not
  // BEHAVIOR. Each entry states which table to query, how to scope it to
  // the project, and which columns can reference cost_codes (by id or by
  // text) — nothing more. It does NOT decide "id-preferred vs
  // text-fallback" or "how many references does this row produce" — that
  // is traversal logic, and it belongs to whichever function is
  // consuming the metadata (runValidation() and getCodeUsage() each own
  // their own traversal, on purpose — see the note above each function).
  // Adding a table means adding one entry here; every consumer that
  // walks this registry sees it automatically, but each still decides
  // for itself how to interpret the columns.
  //
  // Each entry:
  //   module        — display/report label.
  //   table         — the table to query.
  //   fields        — the select() column list.
  //   projectFilter — function(query) => query; applies this table's
  //                    project scoping (direct .eq('project_id', …) or
  //                    an embedded-relation !inner join).
  //   idFields      — array of { field, expectedLevelLabel, checkResourceType,
  //                    descField } for columns that hold a cost_codes.id
  //                    (uuid FK) reference.
  //   textFields    — same shape, for columns that hold a cost_codes.code
  //                    (free text) reference, matched case/whitespace-
  //                    insensitively.
  //   Both arrays may be empty; a table can have any combination of id
  //   and text reference columns (e.g. IPC has one of each — a consumer
  //   deciding to treat them as "id-preferred, text-fallback" is that
  //   consumer's own traversal choice, not something this metadata
  //   dictates).
  //
  //   `field` on an idFields/textFields entry is a column name (string)
  //   or a priority-ordered list of column names (array) — e.g.
  //   Forecast's ['sub_code', 'cost_code']. This is still pure schema
  //   ("the value can live in either column, sub_code first"), not a
  //   transformation: nothing here combines or derives a new value, it
  //   only names which existing column(s) to read. Consumers use the
  //   shared readPriorityField() helper (defined once, near norm()) to
  //   pick the first non-empty one — that helper is a plain data reader,
  //   not row-shaping logic, and is identical for every consumer.
  //
  //   projectFilter is the one function-valued entry in this registry,
  //   and is a deliberate exception to "metadata, not behavior": it
  //   builds part of the OUTGOING QUERY (which project to scope to,
  //   expressed as this table's own join shape), not a transformation of
  //   ROWS once they come back. Every other field here describes the
  //   shape of the data; only projectFilter describes how to ask for it.
  //
  //   PERFORMANCE NOTE: this array (10 small objects) is rebuilt on every
  //   render, since it's declared inside the component body (it closes
  //   over `projectId` for projectFilter). Not memoized deliberately —
  //   the cost of re-creating ten plain objects is negligible compared
  //   to the actual network queries that only run when runValidation()
  //   or getCodeUsage() are explicitly called, so this does not need
  //   useMemo. Worth knowing before "optimizing" it — memoizing this
  //   wouldn't measurably help anything.
  const MODULE_SOURCES = [
    {
      module: 'Purchase Orders',
      table: 'po_line_items',
      fields: 'sub_code, cost_code, description, po_id, purchase_orders!inner(project_id)',
      projectFilter: q => q.eq('purchase_orders.project_id', projectId),
      idFields: [],
      textFields: [{ field: 'sub_code', expectedLevelLabel: 'Sub Code', checkResourceType: true, descField: 'description' }],
    },
    {
      module: 'Commitments (Equipment Rental)',
      table: 'rental_equipment_po_line_items',
      fields: 'equipment_type, description, po_id, rental_equipment_pos!inner(project_id)',
      projectFilter: q => q.eq('rental_equipment_pos.project_id', projectId),
      idFields: [],
      textFields: [{ field: 'equipment_type', expectedLevelLabel: 'Sub Code', checkResourceType: true, descField: 'description' }],
    },
    {
      // NOTE: finance_transactions has no `description` column (verified
      // against the live schema — id, project_id, cost_code_id,
      // entered_by, invoice_ref, vendor, amount_paid, payment_date,
      // period_month, payment_voucher_no, bank_ref, notes, created_at).
      // The original query here requested `description` and returned a
      // 400 from PostgREST as a result — a pre-existing bug, unrelated
      // to this refactor (same fields string existed before
      // MODULE_SOURCES was introduced). Fixed by dropping the
      // nonexistent field rather than guessing a replacement (`notes` or
      // `vendor` might look right but haven't been confirmed as the
      // intended description source) — descField is optional elsewhere
      // in this registry (see Payroll/Accrual, both descField: null), so
      // Finance issues simply surface without a description until the
      // correct column is confirmed.
      module: 'Finance',
      table: 'finance_transactions',
      fields: 'cost_code_id',
      projectFilter: q => q.eq('project_id', projectId),
      idFields: [{ field: 'cost_code_id', expectedLevelLabel: null, checkResourceType: false, descField: null }],
      textFields: [],
    },
    {
      // IPC has BOTH an id column and a text column on the same row —
      // this metadata exposes both; whether a consumer treats them as
      // "id preferred, text fallback" (as runValidation() and
      // getCodeUsage() both choose to do, to avoid double-counting a
      // single line item) is that consumer's own decision.
      module: 'IPC / Actual Cost',
      table: 'ipc_line_items',
      fields: 'sub_code, cost_code_id, description, ipc_id, ipc_certificates!inner(project_id)',
      projectFilter: q => q.eq('ipc_certificates.project_id', projectId),
      idFields: [{ field: 'cost_code_id', expectedLevelLabel: 'Sub Code', checkResourceType: true, descField: 'description' }],
      textFields: [{ field: 'sub_code', expectedLevelLabel: 'Sub Code', checkResourceType: true, descField: 'description' }],
    },
    {
      // Store Issues — added per the Mat-Dew1 investigation. cost_code_id
      // IS present as a column here (unlike above, it's real data), but
      // it is deliberately left OUT of idFields: cost_code_id on this
      // table is a write-time cache that is never re-synchronized after
      // a Sub Code is reparented (see monthlyActualCost.js's own comment
      // on the same issue). Listing it here would let a consumer treat
      // it as an equally-valid reference, which is exactly the bug this
      // investigation traced and fixed — so it is metadata this registry
      // intentionally does not expose as a valid reference column.
      module: 'Store (Actual Cost)',
      table: 'store_issues',
      fields: 'sub_code, cost_code_id',
      projectFilter: q => q.eq('project_id', projectId),
      idFields: [],
      textFields: [{ field: 'sub_code', expectedLevelLabel: 'Sub Code', checkResourceType: true, descField: null }],
    },
    {
      module: 'Payroll (Actual Cost)',
      table: 'payroll_entries',
      fields: 'sub_code_id',
      projectFilter: q => q.eq('project_id', projectId),
      idFields: [{ field: 'sub_code_id', expectedLevelLabel: null, checkResourceType: true, descField: null }],
      textFields: [],
    },
    {
      module: 'Accrual (Actual Cost)',
      table: 'accrual_entries',
      fields: 'sub_code_id',
      projectFilter: q => q.eq('project_id', projectId),
      idFields: [{ field: 'sub_code_id', expectedLevelLabel: null, checkResourceType: true, descField: null }],
      textFields: [],
    },
    {
      // Two independent text columns on the same row.
      module: 'Budget',
      table: 'tender_direct_items',
      fields: 'cost_code, resource_code, resource_description, row_type',
      projectFilter: q => q.eq('project_id', projectId).eq('is_package_header', false),
      idFields: [],
      textFields: [
        { field: 'cost_code', expectedLevelLabel: 'Cost Code', checkResourceType: false, descField: 'resource_description' },
        { field: 'resource_code', expectedLevelLabel: 'Resource Code', checkResourceType: false, descField: 'resource_description' },
      ],
    },
    {
      module: 'Budget (Indirect)',
      table: 'tender_indirect_items',
      fields: 'cost_code, description',
      projectFilter: q => q.eq('project_id', projectId).eq('is_package_header', false),
      idFields: [],
      textFields: [{ field: 'cost_code', expectedLevelLabel: 'Cost Code', checkResourceType: false, descField: 'description' }],
    },
    {
      // Forecast: id column plus a text column that itself falls back
      // between two source columns (sub_code, then cost_code) — modeled
      // declaratively as field: [array of column names in priority
      // order], not a getter function. Metadata says "the value can live
      // in either of these columns, sub_code first" — it does not decide
      // HOW to fall back between them; that's each consuming function's
      // own traversal logic (read field[0] if present, else field[1], …).
      // NOTE: forecast_history has no cost_code_id column — PostgREST
      // confirmed this directly ("column forecast_history.cost_code_id
      // does not exist", hint: "did you mean forecast_history.cost_code")
      // when this query ran against the live database, even though an
      // earlier information_schema.columns check appeared to list
      // cost_code_id for this table — that discrepancy is unresolved and
      // worth investigating separately, but the query error itself is
      // unambiguous and is what this fix follows. Pre-existing bug, same
      // fields string existed before MODULE_SOURCES was introduced.
      // Fixed by dropping idFields entirely and relying only on
      // textFields' existing sub_code/cost_code fallback — the exact
      // pair the hint pointed at.
      module: 'Forecast',
      table: 'forecast_history',
      fields: 'sub_code, cost_code',
      projectFilter: q => q.eq('project_id', projectId),
      idFields: [],
      textFields: [{ field: ['sub_code', 'cost_code'], expectedLevelLabel: 'Sub Code', checkResourceType: true, descField: null }],
    },
  ]

  // ── Validation: detect any code referenced anywhere in the system that either
  // doesn't exist in the current Cost Codes hierarchy, exists but has no parent,
  // or bypasses the Resource Type hierarchy (Cost Code → Resource Code → Sub Code)
  // and therefore won't be classified into the correct Cost Report tab.
  // Read-only — never writes anything. `codes` (already loaded for the tree) is the
  // source of truth for "does this code exist / does it have a parent / is it under a Resource Code".
  async function runValidation() {
    if (!projectId) return
    setValidationLoading(true)
    setValidationError('')
    try {
      // Build lookup maps from the currently-loaded cost_codes tree
      const byId = new Map(codes.map(c => [c.id, c]))
      const byNormCode = new Map(codes.map(c => [norm(c.code), c]))
      // All level-1 Cost Codes and level-2 Sub Codes, for fuzzy-matching suggestions
      const allCodesList = codes.map(c => ({ code: c.code, norm: norm(c.code) }))

      const issues = []
      const seen = new Set() // dedupe by code+module+issue

      function addIssue({ code, description, level, module, issue, suggestion }) {
        const key = `${code}||${module}||${issue}`
        if (seen.has(key)) return
        seen.add(key)
        issues.push({ code, description: description || '', level, module, issue, suggestion: suggestion || '' })
      }

      // Simple Levenshtein distance for "closest match" suggestions — small inputs only (code strings)
      function levenshtein(a, b) {
        const m = a.length, n = b.length
        if (m === 0) return n
        if (n === 0) return m
        const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)])
        for (let j = 0; j <= n; j++) dp[0][j] = j
        for (let i = 1; i <= m; i++) {
          for (let j = 1; j <= n; j++) {
            dp[i][j] = a[i - 1] === b[j - 1]
              ? dp[i - 1][j - 1]
              : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1])
          }
        }
        return dp[m][n]
      }

      // Suggest the closest official code by normalized edit-distance, only if reasonably close
      function suggestClosest(codeText) {
        const target = norm(codeText)
        if (!target) return ''
        let best = null, bestDist = Infinity
        for (const c of allCodesList) {
          const d = levenshtein(target, c.norm)
          if (d < bestDist) { bestDist = d; best = c.code }
        }
        // Only suggest if the edit distance is small relative to the code length —
        // avoids suggesting something wildly unrelated for a totally different code.
        const threshold = Math.max(2, Math.floor(target.length * 0.35))
        return (best && bestDist <= threshold) ? best : ''
      }

      // Check a code that exists ONLY as an id reference (uuid FK) — confirms the id
      // still resolves to a row in cost_codes, that row has a parent if it's not level 1,
      // and — for commitment-bearing modules — that it sits under a Resource Code (level 4)
      // so it will actually be classified into a Cost Report tab (Material/Labour/Subcontract/Equipment).
      function checkById(id, module, fallbackDescription, { checkResourceType = false } = {}) {
        if (!id) return
        const row = byId.get(id)
        if (!row) {
          addIssue({ code: id, description: fallbackDescription, level: '—', module, issue: 'Missing Cost Code' })
          return
        }
        const lvl = parseInt(row.level)
        if (lvl !== 1 && !row.parent_id) {
          addIssue({
            code: row.code, description: row.description, level: lvl === 4 ? 'Resource Code' : 'Sub Code',
            module, issue: lvl === 4 ? 'Missing Parent (Resource Code)' : 'Missing Parent (Sub Code)'
          })
          return
        }
        if (checkResourceType) checkResourceTypeClassification(row, module)
      }

      // Check a code that exists ONLY as free text (sub_code / cost_code / equipment_type text columns) —
      // confirms a matching code (case/whitespace-insensitive) exists in cost_codes at all,
      // that it has a parent, and — for commitment-bearing modules — that it resolves to a
      // Resource Type (Material/Labour/Subcontract/Equipment) via a Resource Code parent.
      function checkByText(codeText, module, fallbackDescription, expectedLevelLabel, { checkResourceType = false } = {}) {
        if (!codeText) return
        const row = byNormCode.get(norm(codeText))
        if (!row) {
          const suggestion = suggestClosest(codeText)
          addIssue({
            code: codeText, description: fallbackDescription, level: expectedLevelLabel || '—', module,
            issue: `Missing ${expectedLevelLabel || 'Sub Code'}`, suggestion
          })
          return
        }
        const lvl = parseInt(row.level)
        if (lvl !== 1 && !row.parent_id) {
          addIssue({
            code: row.code, description: row.description, level: lvl === 4 ? 'Resource Code' : 'Sub Code',
            module, issue: lvl === 4 ? 'Missing Parent (Resource Code)' : 'Missing Parent (Sub Code)'
          })
          return
        }
        if (checkResourceType) checkResourceTypeClassification(row, module)
      }

      // Scenario 3: a code exists and has a parent, but bypasses the Resource Type hierarchy —
      // i.e. it (or its ancestor chain) never passes through a level-4 Resource Code, so it
      // cannot be classified into Material/Labour/Subcontract/Equipment and will silently vanish
      // from those Cost Report tabs even though it's counted in the Grand Total. This ONLY applies
      // to Direct Works — Indirect Works legitimately has no Resource Code level by design.
      function checkResourceTypeClassification(row, module) {
        if (row.cost_type !== 'direct') return // Indirect has no Resource Code level — not an issue
        const lvl = parseInt(row.level)
        if (lvl === 1) return // a Cost Code itself isn't expected to carry a Resource Type
        const parent = row.parent_id ? byId.get(row.parent_id) : null
        const isUnderResourceCode = parent && parseInt(parent.level) === 4
        const parentIsCostCodeDirect = parent && parseInt(parent.level) === 1
        if (!isUnderResourceCode && parentIsCostCodeDirect) {
          addIssue({
            code: row.code, description: row.description, level: 'Sub Code',
            module, issue: 'Unclassified Commitment (bypasses Resource Type — won\'t appear in Material/Labour/Subcontract/Equipment tabs)'
          })
        }
      }

      // Iterate the shared MODULE_SOURCES metadata (see above) — this
      // function owns its own traversal: id-preferred (checking a
      // matched idField skips textFields for that row, so a line item
      // with both a resolved uuid and a raw text code — e.g. IPC — isn't
      // checked twice for the same underlying reference), but every
      // textField that has a value is still checked independently when
      // there's no id match (Budget's cost_code and resource_code are
      // two separate references on one row, not a fallback pair).
      // getCodeUsage() below reads the exact same metadata but makes its
      // own counting decisions — the two functions do not share this
      // traversal logic, only the table/column definitions.
      for (const source of MODULE_SOURCES) {
        let q = supabase.from(source.table).select(source.fields)
        q = source.projectFilter(q)
        const { data } = await q
        for (const r of (data || [])) {
          const idHit = source.idFields.find(f => r[f.field])
          if (idHit) {
            checkById(r[idHit.field], source.module, idHit.descField ? r[idHit.descField] : null, { checkResourceType: idHit.checkResourceType })
            continue
          }
          for (const f of source.textFields) {
            // f.field is either a single column name (string) or a
            // priority-ordered list of column names (array) — metadata
            // says only "check these column(s), in this order"; reading
            // the first non-empty one is this function's own traversal
            // choice (readPriorityField() below, shared as a plain data
            // reader — not a transformation — with getCodeUsage()).
            const value = readPriorityField(r, f.field)
            if (!value) continue
            checkByText(value, source.module, f.descField ? r[f.descField] : null, f.expectedLevelLabel, { checkResourceType: f.checkResourceType })
          }
        }
      }

      // ── Tree Consistency Validation ──────────────────────────────
      // Checks the cost_codes TREE itself, not transactions — runs once
      // over `codes` (already loaded for the tree), not once per line
      // item, unlike every check above. A Sub Code's cost_type is
      // expected to match its Level-1 ancestor's cost_type; a mismatch
      // means the tree currently displays this code under one section
      // (Direct or Indirect) while its own cost_type — which is what
      // every report actually groups by — says the other. This is
      // module-agnostic (not tied to any transaction table), so it's
      // reported with module: 'Cost Codes Tree' rather than any of the
      // MODULE_SOURCES entries above.
      for (const row of codes) {
        if (parseInt(row.level) === 1) continue // a Level-1 code has no ancestor to compare against
        // Walk up to the Level-1 ancestor (mirrors resolveToCostCode()'s
        // traversal elsewhere in this codebase — same 5-hop ceiling).
        let ancestor = row.parent_id ? byId.get(row.parent_id) : null
        let hops = 0
        while (ancestor && parseInt(ancestor.level) !== 1 && ancestor.parent_id && hops < 5) {
          ancestor = byId.get(ancestor.parent_id)
          hops++
        }
        if (!ancestor || parseInt(ancestor.level) !== 1) continue // orphan or unresolved — already flagged elsewhere
        if (row.cost_type !== ancestor.cost_type) {
          addIssue({
            code: row.code, description: row.description, level: parseInt(row.level) === 4 ? 'Resource Code' : 'Sub Code',
            module: 'Cost Codes Tree',
            issue: `Cost Type Mismatch — this code is "${row.cost_type}" but its parent (${ancestor.code} — ${ancestor.description}) is "${ancestor.cost_type}"`
          })
        }
      }

      setValidationResults(issues)
    } catch (err) {
      setValidationError(err.message || String(err))
      setValidationResults(null)
    } finally {
      setValidationLoading(false)
    }
  }

  // ── Audit Trail viewer — reads back what logAudit() writes (Reparent
  // and Cost Type change entries only, entity_type='cost_code', see the
  // comment above logAudit() for the actor_name / previous_status /
  // new_status conventions this display relies on). Read-only, no
  // write/delete here — this is a viewer, not a management screen.
  //
  // NOTE: `created_at` is assumed to exist as a default-populated column
  // (logAudit()'s own insert never sets it explicitly) — this has NOT
  // been directly verified against the live schema the way
  // finance_transactions/forecast_history's actual columns were during
  // this same investigation (both turned out to differ from an earlier,
  // seemingly-authoritative schema check). If this tab 400s on
  // `created_at` specifically, that column may not exist or may be named
  // differently, and should be checked the same way those two were:
  // trigger the request, read the exact PostgREST error message from the
  // Network tab's Response, and correct the column name from that
  // message rather than guessing.
  async function fetchAuditEntries() {
    if (!projectId) return
    setAuditLoading(true)
    setAuditError('')
    try {
      const { data, error } = await supabase.from('audit_trail')
        .select('id, entity_id, action, actor_name, comment, previous_status, new_status, created_at')
        .eq('project_id', projectId)
        .eq('entity_type', 'cost_code')
        .order('created_at', { ascending: false })
        .limit(200)
      if (error) throw error
      setAuditEntries(data || [])
      setAuditLoaded(true)
    } catch (err) {
      console.error('fetchAuditEntries failed', err)
      setAuditError(err.message || String(err))
      setAuditEntries([])
    } finally {
      setAuditLoading(false)
    }
  }

  // ── Dependency-aware Operations: Impact Analysis Service ──────────────
  // Pure data service — no display text, no UI logic. Answers "how many
  // downstream records reference this code right now?" for exactly ONE
  // code, scoped to the operation about to run (Reparent or Cost Type
  // change) — NOT a project-wide health check like runValidation()
  // above (different question: "is anything wrong?" vs. this function's
  // "what will be affected?").
  //
  // Reads the SAME MODULE_SOURCES metadata as runValidation(), but owns
  // its own traversal: a row counts as a match if ANY idField equals the
  // given id, OR any textField (case/whitespace-insensitively) equals
  // the given code — no id-preferred skip is needed here, since this
  // function only counts matches rather than flagging distinct issues.
  //
  // Loads this project's rows for each table and filters in memory, same
  // as runValidation() — deliberately not optimized with server-side
  // .count() yet; correctness and a single code path matter more than
  // raw speed for a first version, and server-side counts can be
  // introduced later without changing this function's return contract
  // if profiling shows it's needed.
  //
  // Cost per call: one query per MODULE_SOURCES entry (currently 10).
  // Calling this function N times (e.g. reparentMultipleSubCodes() below
  // calls it once per selected code) costs N × 10 queries — see that
  // function's own comment for the batch-selection performance tradeoff
  // this implies.
  //
  // Return contract (no UI concerns — the caller decides labels,
  // formatting, and whether/how to show `amount`):
  //   { total: number, modules: [{ key: string, count: number, amount?: number }] }
  // `amount` is intentionally part of the contract from day one, even
  // though this version never sets it (financial exposure per module is
  // not yet computed) — so a future version can populate it without
  // changing what callers destructure.
  async function getCodeUsage(code, id) {
    if (!projectId || (!code && !id)) return { total: 0, modules: [] }
    const normCode = norm(code)
    const counts = {} // module key -> count
    const bump = (key, n = 1) => { if (n > 0) counts[key] = (counts[key] || 0) + n }

    const results = await Promise.all(
      MODULE_SOURCES.map(source => source.projectFilter(supabase.from(source.table).select(source.fields)))
    )

    MODULE_SOURCES.forEach((source, i) => {
      for (const r of (results[i].data || [])) {
        const idMatch = id != null && source.idFields.some(f => r[f.field] === id)
        const textMatch = normCode && source.textFields.some(f => {
          const value = readPriorityField(r, f.field)
          return value && norm(value) === normCode
        })
        if (idMatch || textMatch) bump(source.module)
      }
    })

    const modules = Object.entries(counts).map(([key, count]) => ({ key, count, amount: undefined }))
    const total = modules.reduce((s, m) => s + m.count, 0)
    return { total, modules }
  }

  // ── Dependency-aware Operations: Unified Operation Lifecycle ──────────
  //   Impact Analysis → Confirm → Execute → Operation-specific Action (if any) → Report
  // The lifecycle itself is operation-agnostic: it always runs Impact
  // Analysis (via getCodeUsage(), the shared Service) and always waits
  // for explicit user confirmation before calling `execute`. What
  // happens INSIDE execute is entirely up to the caller — Reparent has
  // no propagation step (reports resolve the current hierarchy
  // dynamically, per the "persist facts, resolve relationships at read
  // time" principle from the Mat-Dew1 investigation); Rename (elsewhere
  // in this file, via propagateSubCodeRename) is the one operation that
  // needs a propagation step, because renaming changes an immutable
  // business fact (the code's own name) rather than a derived
  // hierarchy relationship — this lifecycle does not currently wrap
  // Rename, since its propagate-then-report flow already exists and is
  // independently correct; it is described here only so a future
  // contributor adding a new master-data mutation knows which model
  // ("no propagation" vs "propagation is required") to follow.
  //
  // ── Consistency Warning — deliberately separate from Impact Analysis ──
  // Impact Analysis (getCodeUsage) answers "how many records will be
  // affected?" — a question about TRANSACTIONS. This function answers a
  // different question entirely: "will this specific change leave the
  // tree in a Parent/Child cost_type-mismatched state?" — a question
  // about STRUCTURE, checked against the same logic Tree Consistency
  // Validation uses in runValidation() (walk to the Level-1 ancestor,
  // compare cost_type). Kept as its own function and its own field on
  // pendingOperation (never merged into `usage`) so the two concerns —
  // "what will this affect" vs "will this create a structural
  // inconsistency" — stay independently owned and independently
  // extensible, exactly as agreed: Validation detects the problem
  // globally, this warns about it at the moment of creation, and Impact
  // Analysis's responsibility never expands to cover it.
  //
  // Synchronous and local — reads the already-loaded `codes` tree, no
  // network call, unlike getCodeUsage(). Returns null when there's
  // nothing to warn about (new cost_type matches the resolved parent, or
  // no parent to compare against).
  function computeConsistencyWarning({ subCodeId, newParentId, newCostType }) {
    const row = codes.find(c => c.id === subCodeId)
    if (!row) return null
    // newParentId is passed for Reparent (the code is moving to sit under
    // a different ancestor); newCostType is passed for a Cost Type change
    // (the code's own cost_type is changing, parent stays the same).
    // Never both at once in this file's current operations.
    const effectiveCostType = newCostType != null ? newCostType : row.cost_type
    const effectiveParentId = newParentId != null ? newParentId : row.parent_id
    let ancestor = effectiveParentId ? codes.find(c => c.id === effectiveParentId) : null
    let hops = 0
    while (ancestor && parseInt(ancestor.level) !== 1 && ancestor.parent_id && hops < 5) {
      ancestor = codes.find(c => c.id === ancestor.parent_id)
      hops++
    }
    if (!ancestor || parseInt(ancestor.level) !== 1) return null
    if (effectiveCostType === ancestor.cost_type) return null
    return { parentCode: ancestor.code, parentDescription: ancestor.description, parentCostType: ancestor.cost_type, resultingCostType: effectiveCostType }
  }

  // Opens the Impact Analysis modal immediately (usageLoading: true),
  // runs getCodeUsage() in the background, then updates pendingOperation
  // with the result once it resolves — so the modal is responsive even
  // before the count finishes loading.
  function startDependencyAwareOperation({ operation, codeLabel, code, id, execute, consistencyWarning }) {
    setPendingOperation({ operation, codeLabel, usage: null, usageLoading: true, execute, consistencyWarning: consistencyWarning || null })
    getCodeUsage(code, id).then(usage => {
      setPendingOperation(prev => (prev && prev.execute === execute) ? { ...prev, usage, usageLoading: false } : prev)
    })
  }

  async function confirmPendingOperation() {
    if (!pendingOperation) return
    await pendingOperation.execute()
    setPendingOperation(null)
  }

  function cancelPendingOperation() {
    setPendingOperation(null)
  }

  async function fetchResourceDict() {
    setDictLoading(true)
    let query = supabase
      .from('resource_dictionary')
      .select('*')
      .order('sort_order', { ascending: true, nullsFirst: false })
      .order('code') // fallback for rows without sort_order
    if (projectId) query = query.eq('project_id', projectId)
    const { data } = await query
    setResourceDict(data || [])
    setDictLoading(false)
  }

  // parentId = the Cost Code (level 1) this Resource Code will live under.
  // level 3 (Sub Code) is untouched by this flow — it keeps its own free-text add via re-parenting UI.
  function openNew(parentId = null) {
    if (parentId) {
      // Adding a level-2 Resource Code under a Cost Code → open the dictionary picker instead of free text
      setResourcePicker({ parentId })
      setResourcePickerSearch('')
      return
    }
    setForm({
      code: '', description: '', unit: '',
      parent_id: '',
      level: '1',
      cost_type: 'direct',
      is_active: true
    })
    setEditCode(null)
    setShowModal(true)
  }

  async function attachResourceCodes(parentId, dictItems) {
    const parentCostType = codes.find(c => c.id === parentId)?.cost_type || 'direct'
    const payloads = dictItems.map(dictItem => ({
      code: dictItem.code,
      description: dictItem.description,
      unit: dictItem.unit || null,
      parent_id: parentId,
      level: 4, // Resource Code — deliberately NOT 2 (legacy Sub Code level used by 8+ other pages for dropdowns)
      cost_type: parentCostType,
      is_active: true,
      resource_dict_code: dictItem.code,
      project_id: projectId || null,
    }))
    const { error } = await supabase.from('cost_codes').insert(payloads)
    if (error) { alert(error.message); return }
    setResourcePicker(null)
    fetchCodes(null, { silent: true })
  }

  function openEdit(c) {
    setForm({
      code: c.code || '',
      description: c.description || '',
      unit: c.unit || '',
      parent_id: c.parent_id || '',
      level: String(c.level || '2'),
      cost_type: c.cost_type || 'direct',
      is_active: c.is_active !== false
    })
    setEditCode(c)
    setShowModal(true)
  }

  // Re-parent an existing Sub Code (currently sitting directly under a Cost Code)
  // so it moves under a Resource Code instead. Only parent_id changes — level STAYS 2,
  // because 8+ other pages (PR, PO, RFQ, PettyCash, Budget, CostControl, DirectForecast, BOQ)
  // query cost_codes with level===2 to populate their Sub Code dropdowns. Changing level would
  // silently empty those dropdowns. code/description/unit are untouched either way.
  //
  // Goes through the Unified Operation Lifecycle: this function no
  // longer writes to cost_codes directly — it starts the lifecycle,
  // which runs Impact Analysis (getCodeUsage) and shows the modal; the
  // actual UPDATE only runs from confirmPendingOperation() once the user
  // confirms. Reparent has no Operation-specific Action (no propagation
  // step) — see the note above startDependencyAwareOperation() for why.
  // Deliberately NOT async — the lifecycle is fire-and-forget from every
  // call site (all onClick handlers); nothing awaits its return value or
  // treats it as a Promise. Kept synchronous rather than async to make
  // that "don't await this" contract visible at the call site too.
  function reparentSubCode(subCodeId, newResourceParentId) {
    const row = codes.find(c => c.id === subCodeId)
    const oldParentCode = row?.parent_id ? codes.find(c => c.id === row.parent_id)?.code : null
    const newParentCode = codes.find(c => c.id === newResourceParentId)?.code || newResourceParentId
    startDependencyAwareOperation({
      operation: 'reparent',
      codeLabel: row?.code || subCodeId,
      code: row?.code, id: subCodeId,
      consistencyWarning: computeConsistencyWarning({ subCodeId, newParentId: newResourceParentId }),
      execute: async () => {
        const { error } = await supabase
          .from('cost_codes')
          .update({ parent_id: newResourceParentId })
          .eq('id', subCodeId)
        if (error) { alert(error.message); return }
        // Audit: the changed value here is a parent_id (uuid), not a
        // status, so the before/after Cost Code names go in `comment` —
        // see the note above logAudit() for why previous_status/
        // new_status aren't used for this operation.
        logAudit(subCodeId, 'cost_code', 'reparent', actorName,
          `${row?.code || subCodeId}: parent ${oldParentCode || '(none)'} → ${newParentCode}`, null, null, projectId)
        fetchCodes(null, { silent: true })
      },
    })
  }

  // Batch version — link multiple Sub Codes to the same Resource Code in one call.
  // Same lifecycle as reparentSubCode() above, but Impact Analysis is
  // summed across every selected code (getCodeUsage() has no native
  // batch mode — the Service contract stays single-code, so this
  // function runs it once per id and merges the results client-side,
  // per the same "consumer owns its own traversal" principle used
  // elsewhere for this Service).
  //
  // KNOWN PERFORMANCE CHARACTERISTIC (not a defect, but worth knowing
  // before touching this): getCodeUsage() fetches all of this project's
  // rows across all 10 MODULE_SOURCES tables per call (see that
  // function's own comment on why — correctness over premature
  // optimization). Calling it once per selected code here means N
  // selected codes cost N × 10 queries, run in parallel via Promise.all.
  // For a large multi-select (dozens of codes) on a large project this
  // is the most expensive path in this file. If that becomes a real
  // problem, the fix belongs in getCodeUsage() (a genuine batch mode, or
  // server-side counts) rather than in this function — see the note atop
  // getCodeUsage() about that same tradeoff being deferred until
  // profiling shows it's needed.
  //
  // pendingOperation is matched back by codeLabel (not by an execute
  // reference, unlike startDependencyAwareOperation() above) because
  // execute doesn't exist yet when the async getCodeUsage() batch is
  // kicked off. This means two batch-reparent operations started with
  // the SAME selection size (e.g. two separate "5 Sub Codes" reparents)
  // in unrealistically-fast succession could theoretically resolve out
  // of order and overwrite each other's pendingOperation — not possible
  // through the normal UI (the modal is a full-screen overlay that
  // blocks starting a second operation while one is open), so this is
  // a latent risk rather than a reachable bug today.
  function reparentMultipleSubCodes(subCodeIds, newResourceParentId) {
    if (!subCodeIds || subCodeIds.length === 0) return
    const rows = subCodeIds.map(id => codes.find(c => c.id === id)).filter(Boolean)
    const codeLabel = rows.length === 1 ? rows[0].code : `${rows.length} Sub Codes`
    const newParentCode = codes.find(c => c.id === newResourceParentId)?.code || newResourceParentId
    // Consistency Warning across a multi-select: each code is checked
    // independently against computeConsistencyWarning() (same function
    // reparentSubCode() uses), then collected into a single list — one
    // warning entry per code that would mismatch, not a single merged
    // warning, so each affected code stays individually identifiable.
    const consistencyWarnings = rows
      .map(r => ({ code: r.code, warning: computeConsistencyWarning({ subCodeId: r.id, newParentId: newResourceParentId }) }))
      .filter(w => w.warning)
    setPendingOperation({ operation: 'reparent', codeLabel, usage: null, usageLoading: true, execute: null, consistencyWarning: consistencyWarnings.length > 0 ? consistencyWarnings : null })
    Promise.all(rows.map(r => getCodeUsage(r.code, r.id))).then(results => {
      const merged = {} // key -> count
      for (const u of results) for (const m of u.modules) merged[m.key] = (merged[m.key] || 0) + m.count
      const modules = Object.entries(merged).map(([key, count]) => ({ key, count, amount: undefined }))
      const usage = { total: modules.reduce((s, m) => s + m.count, 0), modules }
      const execute = async () => {
        const { error } = await supabase
          .from('cost_codes')
          .update({ parent_id: newResourceParentId })
          .in('id', subCodeIds)
        if (error) { alert(error.message); return }
        // Audit: one row per code, not one merged row, so each entry
        // stays individually meaningful (own old/new parent, own id).
        for (const r of rows) {
          const oldParentCode = r.parent_id ? codes.find(c => c.id === r.parent_id)?.code : null
          logAudit(r.id, 'cost_code', 'reparent', actorName,
            `${r.code}: parent ${oldParentCode || '(none)'} → ${newParentCode}`, null, null, projectId)
        }
        fetchCodes(null, { silent: true })
      }
      setPendingOperation(prev => (prev && prev.codeLabel === codeLabel) ? { ...prev, usage, usageLoading: false, execute } : prev)
    })
  }

  // Not auto-updated by the rename_sub_code_project RPC (linked via quotation_id, not project_id):
  const SUB_CODE_TABLES_MANUAL_REVIEW = ['quotation_line_items', 'equipment_quotation_line_items']

  const [renaming, setRenaming] = useState(false)
  const [deletingIds, setDeletingIds] = useState(new Set())
  const [togglingIds, setTogglingIds] = useState(new Set())

  async function propagateSubCodeRename(oldCode, newCode, pid) {
    if (!oldCode || !newCode || oldCode === newCode || !pid) return { updated: 0, breakdown: {}, errors: [] }
    const { data, error } = await supabase.rpc('rename_sub_code_project', {
      p_project_id: pid,
      p_old_code: oldCode,
      p_new_code: newCode,
    })
    if (error) return { updated: 0, breakdown: {}, errors: [error.message] }
    return { updated: data?.total_updated || 0, breakdown: data?.breakdown || {}, errors: [] }
  }

  // Split into the actual write (executeSaveCode) and the entry point
  // (saveCode) that decides whether a Cost Type change on an EXISTING
  // code needs to go through the Unified Operation Lifecycle first.
  // Creating a new code, or editing one without changing cost_type,
  // still saves immediately — Impact Analysis only applies when
  // cost_type is about to change on a code that may already have
  // recorded transactions (same principle as Reparent: this changes how
  // existing history is grouped in every report that reads it).
  async function executeSaveCode(payload, previousCostType) {
    let error
    const oldCode = editCode?.code || null
    const isRename = editCode && oldCode && oldCode !== payload.code
    if (editCode) {
      ;({ error } = await supabase.from('cost_codes').update(payload).eq('id', editCode.id))
    } else {
      ;({ error } = await supabase.from('cost_codes').insert(payload))
    }
    if (error) { alert(error.message); return }

    // Audit: only for an actual Cost Type change (previousCostType is
    // only passed when saveCode() detected one) — cost_type IS a short
    // classification string, so previous_status/new_status are used
    // directly here, unlike Reparent's parent_id (uuid) case above.
    if (previousCostType != null) {
      logAudit(editCode.id, 'cost_code', 'cost_type_change', actorName,
        editCode.code, previousCostType, payload.cost_type, projectId)
    }

    if (isRename) {
      setRenaming(true)
      const { updated, breakdown, errors } = await propagateSubCodeRename(oldCode, payload.code, projectId)
      setRenaming(false)
      let msg = ''
      if (errors.length) {
        msg += `تم تعديل الكود، لكن حصل خطأ في تحديث بعض الجداول:\n${errors.join('\n')}\n\n`
      } else if (updated > 0) {
        const tableNames = {
          budget_summary: 'Budget Summary', cf_package_dictionary: 'Cash Flow Packages', cf_payment_entries: 'Payment Entries',
          direct_budget_items: 'Direct Budget', equipment_contracts: 'Equipment Contracts', forecast_eac: 'Forecast EAC',
          forecast_history: 'Forecast History', indirect_budget_items: 'Indirect Budget', labour_productivity_entries: 'Labour Productivity',
          labour_rates: 'Labour Rates', petty_cash_vouchers: 'Petty Cash', purchase_orders: 'Purchase Orders (Equipment PO)',
          purchase_requisitions: 'Purchase Requisitions (Equipment PR)', rental_equipment_pos: 'Rental Equipment POs',
          store_issues: 'Store', subcontracts: 'Subcontracts', po_line_items: 'Purchase Orders (Material Lines)',
          pr_line_items: 'Purchase Requisitions (Material Lines)', equipment_pr_line_items: 'Purchase Requisitions (Equipment Lines)',
          ipc_line_items: 'IPC',
        }
        const lines = Object.entries(breakdown || {})
          .map(([table, count]) => `• ${tableNames[table] || table}: ${count} سجل`)
          .join('\n')
        msg += `تم تحديث الاسم في ${updated} سجل إجمالي:\n${lines}\n\n`
      }
      msg += `⚠️ تنبيه: عروض الأسعار (Quotations) لا يتم تحديثها تلقائيًا — راجعها يدويًا لو مستخدم فيها الكود القديم "${oldCode}".`
      alert(msg)
    }

    setShowModal(false)
    fetchCodes(null, { silent: true })
  }

  async function saveCode() {
    if (!form.code || !form.description) {
      alert('Code and Description are required')
      return
    }
    if (renaming) return
    const payload = {
      code: form.code.trim(),
      description: form.description.trim(),
      unit: form.unit.trim(),
      parent_id: form.parent_id || null,
      level: [1,2,4].includes(parseInt(form.level)) ? parseInt(form.level) : 2,
      cost_type: form.cost_type,
      is_active: form.is_active,
      project_id: projectId || null
    }

    // Cost Type change on an EXISTING code — Operation-specific Action:
    // none (same as Reparent — reports resolve cost_type from the
    // current cost_codes row at read time, nothing is propagated). Goes
    // through the lifecycle so the user sees Impact Analysis first.
    const isCostTypeChange = editCode && editCode.cost_type !== payload.cost_type
    if (isCostTypeChange) {
      startDependencyAwareOperation({
        operation: 'costType',
        codeLabel: editCode.code,
        code: editCode.code, id: editCode.id,
        // parent_id is unchanged by a Cost Type edit (only cost_type
        // itself moves) — so newParentId is omitted and
        // computeConsistencyWarning() compares the NEW cost_type against
        // the code's EXISTING ancestor.
        consistencyWarning: computeConsistencyWarning({ subCodeId: editCode.id, newCostType: payload.cost_type }),
        execute: () => executeSaveCode(payload, editCode.cost_type),
      })
      return
    }

    await executeSaveCode(payload)
  }

  async function deleteCode(id) {
    if (deletingIds.has(id)) return // already in progress — ignore repeated clicks
    const hasChildren = codes.some(c => c.parent_id === id)
    if (hasChildren) { alert('لا يمكن حذف كود له sub codes. احذف الـ sub codes أولاً.'); return }
    if (!window.confirm('حذف الكود؟')) return
    setDeletingIds(prev => new Set(prev).add(id))
    try {
      await supabase.from('cost_codes').delete().eq('id', id)
      await fetchCodes(null, { silent: true })
    } finally {
      setDeletingIds(prev => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  async function toggleActive(id, current) {
    if (togglingIds.has(id)) return // already in progress — ignore repeated clicks
    setTogglingIds(prev => new Set(prev).add(id))
    try {
      await supabase.from('cost_codes').update({ is_active: !current }).eq('id', id)
      await fetchCodes(null, { silent: true })
    } finally {
      setTogglingIds(prev => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  // ── Resource Dictionary CRUD ───────────────────────────────
  function openDictNew() {
    setDictForm({ code: '', resource_type: 'material', description: '', unit: '', is_active: true })
    setEditDictItem(null)
    setShowDictModal(true)
  }

  function openDictEdit(item) {
    setDictForm({
      code: item.code || '',
      resource_type: item.resource_type || 'material',
      description: item.description || '',
      unit: item.unit || '',
      is_active: item.is_active !== false
    })
    setEditDictItem(item)
    setShowDictModal(true)
  }

  async function saveDictItem() {
    if (!dictForm.code || !dictForm.description) {
      alert('Code and Description are required')
      return
    }
    const payload = {
      code: dictForm.code.trim(),
      resource_type: dictForm.resource_type,
      description: dictForm.description.trim(),
      unit: dictForm.unit.trim() || null,
      is_active: dictForm.is_active,
    }
    let error
    if (editDictItem) {
      ;({ error } = await supabase.from('resource_dictionary').update(payload).eq('id', editDictItem.id))
    } else {
      // New manual entries go to the end of the list
      const maxSort = resourceDict.reduce((max, d) => Math.max(max, d.sort_order || 0), 0)
      ;({ error } = await supabase.from('resource_dictionary').insert({ ...payload, sort_order: maxSort + 1, project_id: projectId || null }))
    }
    if (error) { alert(error.message); return }
    setShowDictModal(false)
    fetchResourceDict()
  }

  async function deleteDictItem(id) {
    const inUse = codes.some(c => c.resource_dict_code && resourceDict.find(d => d.id === id)?.code === c.resource_dict_code)
    if (inUse) { alert('لا يمكن حذف هذا الكود لأنه مستخدم في Cost Codes بالفعل.'); return }
    if (!window.confirm('حذف الكود من الـ Dictionary؟')) return
    await supabase.from('resource_dictionary').delete().eq('id', id)
    fetchResourceDict()
  }

  async function toggleDictActive(id, current) {
    await supabase.from('resource_dictionary').update({ is_active: !current }).eq('id', id)
    fetchResourceDict()
  }

  function downloadDictTemplate() {
    const ws = XLSX.utils.json_to_sheet([
      { code: 'SC-DEW-01', resource_type: 'subcontract', description: 'Dewatering Pump Installation & Operation Subcontractor', unit: 'Month' },
      { code: 'EQP-DEW-01', resource_type: 'equipment', description: 'Dewatering Pumps Rental (incl. Fuel)', unit: 'Month' },
      { code: 'MAT-CON-01', resource_type: 'material', description: 'Ready-Mix Concrete Supply', unit: 'm³' },
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Resource Dictionary')
    XLSX.writeFile(wb, 'resource_dictionary_template.xlsx')
  }

  function handleDictFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setDictUploadError('')
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary' })
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
        if (rows.length === 0) { setDictUploadError('الملف فاضي'); return }
        setDictUploadRows(rows)
      } catch { setDictUploadError('خطأ في قراءة الملف') }
    }
    reader.readAsBinaryString(file)
  }

  async function confirmDictUpload() {
    setDictUploading(true)
    try {
      // Preserve the exact row order from the Excel file (e.g. grouped by Item, not alphabetical by code)
      const baseSort = resourceDict.reduce((max, d) => Math.max(max, d.sort_order || 0), 0)
      const rows = dictUploadRows
        .filter(r => String(r.code||'').trim() && String(r.description||'').trim())
        .map((r, idx) => ({
          code: String(r.code).trim(),
          resource_type: ['material','labour','subcontract','equipment'].includes(String(r.resource_type||'').toLowerCase())
            ? String(r.resource_type).toLowerCase() : 'material',
          description: String(r.description).trim(),
          unit: r.unit ? String(r.unit).trim() : null,
          is_active: true,
          sort_order: baseSort + idx + 1,
          project_id: projectId || null,
        }))
      for (let i = 0; i < rows.length; i += 50) {
        // onConflict on (code, project_id) — same code text can exist independently in different projects
        const { error } = await supabase.from('resource_dictionary').upsert(rows.slice(i,i+50), { onConflict: 'code,project_id' })
        if (error) console.error('Dict batch:', error.message)
      }
      alert(`✅ تم رفع ${rows.length} كود إلى الـ Resource Dictionary (بنفس ترتيب الملف)`)
      setDictUploadRows([]); setShowDictUpload(false)
      if (dictFileRef.current) dictFileRef.current.value = ''
      await fetchResourceDict()
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setDictUploading(false)
    }
  }

  function exportDictToExcel() {
    const rows = resourceDict.map(d => ({
      'Code': d.code,
      'Type': d.resource_type,
      'Description': d.description,
      'Unit': d.unit,
      'Active': d.is_active ? 'Yes' : 'No'
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Resource Dictionary')
    XLSX.writeFile(wb, 'resource_dictionary.xlsx')
  }

  function toggleExpand(id) {
    setExpandedParents(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleExpandResource(id) {
    setExpandedResources(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function expandAll() {
    setExpandedParents(new Set(codes.filter(c => c.level === 1).map(c => c.id)))
  }

  function collapseAll() {
    setExpandedParents(new Set())
  }

  // ===================== UPLOAD =====================
  function downloadTemplate() {
    const ws = XLSX.utils.json_to_sheet([
      { code: '31 23 19', description: 'Temporary Dewatering', unit: '', level: 1, cost_type: 'direct', parent_code: '' },
      { code: 'SC-DEW-01', description: '(يُملأ تلقائياً من Resource Dictionary)', unit: '', level: 4, cost_type: 'direct', parent_code: '31 23 19', resource_dict_code: 'SC-DEW-01' },
      { code: 'S-C.Dew.1', description: 'Action-subcontractor', unit: 'LS', level: 2, cost_type: 'direct', parent_code: 'SC-DEW-01' },
      { code: 'Mat-Dew2', description: 'Pumps', unit: 'Item', level: 2, cost_type: 'direct', parent_code: 'SC-DEW-01' },
      { code: '50.001', description: 'Site Office Rent', unit: 'Month', level: 1, cost_type: 'indirect', parent_code: '' },
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Cost Codes')
    XLSX.writeFile(wb, 'cost_codes_template.xlsx')
  }

  function downloadNRM1() {
    fetch('/NRM1_CostCodes_QUBEX_Template.xlsx').catch(() => {})
    // Build inline since no static file guaranteed
    const rows = [
      {code:'1',description:'Facilitating Works',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'1.1',description:'Toxic / Hazardous / Contaminated Material Treatment',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.1.1',description:'Asbestos Removal',unit:'m²',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.1.2',description:'Contaminated Ground Treatment',unit:'m³',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.1.3',description:'Other Hazardous Materials',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.2',description:'Major Demolition Works',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.2.1',description:'Demolition of Buildings',unit:'m²',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.2.2',description:'Demolition of Structures',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.3',description:'Specialist Groundworks',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.3.1',description:'Site Dewatering',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.3.2',description:'Ground Stabilisation / Soil Improvement',unit:'m³',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.3.3',description:'Temporary Sheet Piling / Cofferdams',unit:'m²',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.4',description:'Temporary Diversion Works',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'1.5',description:'Extraordinary Site Investigation Works',unit:'item',level:2,cost_type:'direct',parent_code:'1'},
      {code:'2',description:'Substructure',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'2.1',description:'Ground Floor Construction',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.1.1',description:'Excavation & Earthworks',unit:'m³',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.1.2',description:'Hardcore / Granular Sub-Base',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.1.3',description:'Concrete Ground-Bearing Slab',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.1.4',description:'Suspended Ground Floor',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.2',description:'Basement Excavation',unit:'m³',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.2.1',description:'Excavation',unit:'m³',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.2.2',description:'Retaining Walls',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.3',description:'Foundations',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.3.1',description:'Strip Foundations',unit:'m',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.3.2',description:'Pad Foundations',unit:'nr',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.3.3',description:'Raft Foundations',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.3.4',description:'Pile Foundations',unit:'nr',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.3.5',description:'Pile Caps & Ground Beams',unit:'m³',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.3.6',description:'Underpinning',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.4',description:'Basement Retaining Walls',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.5',description:'Basement Floors',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.5.1',description:'Basement Slab',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.5.2',description:'Basement Waterproofing (Floor)',unit:'m²',level:2,cost_type:'direct',parent_code:'2'},
      {code:'2.6',description:'Specialist Substructure',unit:'item',level:2,cost_type:'direct',parent_code:'2'},
      {code:'3',description:'Superstructure',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'3.1',description:'Frame',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.1.1',description:'Steel Framing',unit:'tonne',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.1.2',description:'Space Deck / Space Frame',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.1.3',description:'Concrete Framework',unit:'m³',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.1.4',description:'Precast Concrete Frame',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.1.5',description:'Timber Frame',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.1.6',description:'Composite Frame',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.2',description:'Upper Floors',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.2.1',description:'RC In-Situ Upper Floors',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.2.2',description:'Precast Concrete Floors',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.2.3',description:'Composite Metal Deck Floors',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.3',description:'Roof',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.3.1',description:'Roof Structure',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.3.2',description:'Roof Coverings',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.3.3',description:'Roof Drainage',unit:'item',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.3.4',description:'Roof Lights / Glazed Roof',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.3.5',description:'Roof Features',unit:'item',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.4',description:'Stairs & Ramps',unit:'nr',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.4.1',description:'Concrete Stairs',unit:'flight',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.4.2',description:'Metal Stairs',unit:'flight',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.4.3',description:'Timber Stairs',unit:'flight',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.4.4',description:'Ramps',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.5',description:'External Walls',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.5.1',description:'External Masonry Walls',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.5.2',description:'External Framed Walls',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.5.3',description:'External Walls – Other',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.5.4',description:'Solar Shading / Louvres',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.6',description:'Windows & External Doors',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.6.1',description:'Windows',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.6.2',description:'External Doors',unit:'nr',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.6.3',description:'Curtain Walling / Shopfronts',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.7',description:'Internal Walls & Partitions',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.7.1',description:'Brick / Block Partitions',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.7.2',description:'Drywall / Stud Partitions',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.7.3',description:'Demountable Partitions',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.7.4',description:'Structural Internal Walls',unit:'m²',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.8',description:'Internal Doors',unit:'nr',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.8.1',description:'Flush Doors',unit:'nr',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.8.2',description:'Fire Doors',unit:'nr',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.8.3',description:'Sliding / Folding Doors',unit:'nr',level:2,cost_type:'direct',parent_code:'3'},
      {code:'3.8.4',description:'Roller Shutters / Overhead Doors',unit:'nr',level:2,cost_type:'direct',parent_code:'3'},
      {code:'4',description:'Internal Finishes',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'4.1',description:'Wall Finishes',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.1.1',description:'Plaster / Render',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.1.2',description:'Dry Lining / Plasterboard',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.1.3',description:'Ceramic / Porcelain Tiling',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.1.4',description:'Stone Cladding (Internal)',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.1.5',description:'Painting & Decorating (Walls)',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.1.6',description:'Feature / Specialist Wall Finishes',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2',description:'Floor Finishes',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.1',description:'Screeds / Levelling Compound',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.2',description:'Ceramic / Porcelain Tiling (Floor)',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.3',description:'Natural Stone Flooring',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.4',description:'Hardwood / Engineered Timber Flooring',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.5',description:'Carpet',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.6',description:'Vinyl / LVT / Linoleum',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.7',description:'Raised Access Floor',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.2.8',description:'Epoxy / Resin Floors',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.3',description:'Ceiling Finishes',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.3.1',description:'Plaster / Skim to Soffit',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.3.2',description:'Suspended Grid Ceilings',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.3.3',description:'Plasterboard Suspended Ceilings',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.3.4',description:'Specialist Ceilings',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'4.3.5',description:'Painting & Decorating (Ceilings)',unit:'m²',level:2,cost_type:'direct',parent_code:'4'},
      {code:'5',description:'Fittings, Furnishings & Equipment (FF&E)',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'5.1',description:'General FF&E',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.2',description:'Joinery Fittings & Fixtures',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.2.1',description:'Kitchen Units & Worktops',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.2.2',description:'Reception Desks & Built-In Joinery',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.2.3',description:'Wardrobes / Storage Units',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.3',description:'Signs / Graphics',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.4',description:'Equipment – Specialist',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.5',description:'Ironmongery',unit:'nr',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.6',description:'Soft Furnishings',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'5.7',description:'Artwork',unit:'item',level:2,cost_type:'direct',parent_code:'5'},
      {code:'6',description:'Services (MEP)',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'6.1',description:'Sanitary Installations',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.1.1',description:'Sanitary Fittings',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.1.2',description:'Showers / Shower Trays',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.1.3',description:'Disabled / Accessible Fittings',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.2',description:'Services Equipment',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.3',description:'Disposal Installations',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.3.1',description:'Above-Ground Drainage',unit:'m',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.3.2',description:'Below-Ground Drainage (Foul)',unit:'m',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.3.3',description:'Below-Ground Drainage (Surface Water)',unit:'m',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.3.4',description:'Grease Traps / Separators',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.4',description:'Water Installations',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.4.1',description:'Cold Water Service',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.4.2',description:'Hot Water Service',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.4.3',description:'Heating System (LTHW)',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.4.4',description:'Chilled Water System',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.5',description:'Heat Source',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.6',description:'Space Cooling & Air-Conditioning',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.6.1',description:'Chilled Water Plant',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.6.2',description:'Packaged AC (Split/VRF)',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.6.3',description:'Fan Coil Units',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.7',description:'Ventilation',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.7.1',description:'Supply & Extract Air System',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.7.2',description:'Smoke Extract / Pressurisation',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.7.3',description:'Kitchen Extract',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.8',description:'Electrical Installations',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.8.1',description:'HV Installation',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.8.2',description:'LV Switchgear & Distribution Boards',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.8.3',description:'Busbar Trunking',unit:'m',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.8.4',description:'Containment (Cable Trays, Conduit)',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.8.5',description:'Final Circuit Wiring',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.8.6',description:'Generators / UPS',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.9',description:'Fuel Installations',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.10',description:'Lift & Conveyor Installations',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.10.1',description:'Passenger Lifts',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.10.2',description:'Goods / Service Lifts',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.10.3',description:'Escalators',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.10.4',description:'Moving Walkways',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.10.5',description:'Dumbwaiters',unit:'nr',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.11',description:'Fire & Lightning Protection',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.11.1',description:'Sprinkler System',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.11.2',description:'Dry / Wet Risers',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.11.3',description:'Gaseous Suppression (FM200)',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.11.4',description:'Lightning Protection',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.12',description:'Communication & Security',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.12.1',description:'IT / Data Cabling Infrastructure',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.12.2',description:'Telephone / PA / Nurse Call',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.12.3',description:'CCTV & Access Control',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.12.4',description:'Intruder Alarm',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.12.5',description:'Audio-Visual (AV) Systems',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.13',description:'Specialist Installations',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.13.1',description:'Building Management System (BMS)',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.13.2',description:'Medical Gas Installations',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.13.3',description:'Pneumatic Tube Systems',unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'6.14',description:"Builder's Work in Connection (BWIC)",unit:'item',level:2,cost_type:'direct',parent_code:'6'},
      {code:'7',description:'Prefabricated Buildings & Building Units',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'7.1',description:'Prefabricated Buildings',unit:'m²',level:2,cost_type:'direct',parent_code:'7'},
      {code:'7.2',description:'Prefabricated Building Units',unit:'nr',level:2,cost_type:'direct',parent_code:'7'},
      {code:'8',description:'Works to Existing Buildings',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'8.1',description:'Minor Demolition & Alteration Works',unit:'item',level:2,cost_type:'direct',parent_code:'8'},
      {code:'8.2',description:'Repairs to Existing Services',unit:'item',level:2,cost_type:'direct',parent_code:'8'},
      {code:'8.3',description:'Damp-Proof Courses / Fungal / Beetle Eradication',unit:'m²',level:2,cost_type:'direct',parent_code:'8'},
      {code:'8.4',description:'Facade Retention',unit:'m²',level:2,cost_type:'direct',parent_code:'8'},
      {code:'8.5',description:'Cleaning Existing Surfaces',unit:'m²',level:2,cost_type:'direct',parent_code:'8'},
      {code:'8.6',description:'Renovation Works',unit:'item',level:2,cost_type:'direct',parent_code:'8'},
      {code:'9',description:'External Works',unit:'',level:1,cost_type:'direct',parent_code:''},
      {code:'9.1',description:'Site Preparation Works',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.2',description:'Roads, Paths, Pavings & Surfacings',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.2.1',description:'Flexible Paving (Asphalt)',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.2.2',description:'Rigid Paving (Concrete)',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.2.3',description:'Blockwork Paving',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.2.4',description:'Natural Stone Paving',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.2.5',description:'Gravel / Loose Surfaces',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.2.6',description:'Kerbs, Edgings, Channels',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.3',description:'Soft Landscaping, Planting & Irrigation',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.3.1',description:'Topsoil & Cultivating',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.3.2',description:'Seeding & Turfing',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.3.3',description:'Planting (Trees, Shrubs, Groundcover)',unit:'nr',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.3.4',description:'Irrigation System',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.4',description:'Fencing, Railings & Walls',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.4.1',description:'Fencing & Security Fencing',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.4.2',description:'Railings & Gates',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.4.3',description:'Retaining Walls (External)',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.4.4',description:'Boundary Walls (Masonry)',unit:'m²',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.5',description:'External Fixtures',unit:'nr',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.6',description:'External Drainage',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.6.1',description:'External Foul Drainage',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.6.2',description:'External Surface Water Drainage',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.6.3',description:'SuDS Attenuation',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.7',description:'External Services',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.7.1',description:'Water Mains & Connections',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.7.2',description:'Gas Mains & Connections',unit:'m',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.7.3',description:'Electricity Supply & Connections',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.7.4',description:'Telecommunications / Data',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.7.5',description:'External Lighting',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.8',description:'Minor Buildings & Structures',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'9.9',description:'Temporary Works (Enabling)',unit:'item',level:2,cost_type:'direct',parent_code:'9'},
      {code:'10',description:"Main Contractor's Preliminaries",unit:'',level:1,cost_type:'indirect',parent_code:''},
      {code:'10.1',description:"Employer's Requirements",unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2',description:"Main Contractor's Cost Items",unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.1',description:'Site Management & Staff',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.2',description:'Site Establishment',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.3',description:'Temporary Works (Construction)',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.4',description:'Mechanical Plant & Equipment',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.5',description:'Temporary Services',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.6',description:'Security',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.7',description:'Health, Safety & Environmental',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.8',description:'Cleaning',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.9',description:'Testing & Commissioning (Prelims)',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.10',description:'Fees, Charges & Levies',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'10.2.11',description:'Insurance',unit:'item',level:2,cost_type:'indirect',parent_code:'10'},
      {code:'11',description:"Main Contractor's Overheads & Profit",unit:'',level:1,cost_type:'indirect',parent_code:''},
      {code:'11.1',description:"Main Contractor's Overheads",unit:'%',level:2,cost_type:'indirect',parent_code:'11'},
      {code:'11.2',description:"Main Contractor's Profit",unit:'%',level:2,cost_type:'indirect',parent_code:'11'},
      {code:'12',description:'Project / Design Team Fees',unit:'',level:1,cost_type:'indirect',parent_code:''},
      {code:'12.1',description:"Consultants' Fees",unit:'%',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'12.1.1',description:'Architect Fees',unit:'%',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'12.1.2',description:'Structural Engineer Fees',unit:'%',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'12.1.3',description:'MEP / Building Services Engineer Fees',unit:'%',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'12.1.4',description:'Project Manager / Cost Manager Fees',unit:'%',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'12.1.5',description:'Other Specialist Consultants',unit:'%',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'12.2',description:"Main Contractor's Pre-Construction Fees",unit:'item',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'12.3',description:"Main Contractor's Design Fees",unit:'item',level:2,cost_type:'indirect',parent_code:'12'},
      {code:'13',description:'Other Development / Project Costs',unit:'',level:1,cost_type:'indirect',parent_code:''},
      {code:'13.1',description:'Hoardings, Fencing & Advertising',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'13.2',description:'Surveys (Pre-Construction)',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'13.3',description:'Building Information Modelling (BIM)',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'13.4',description:'Statutory Fees & Charges',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'13.5',description:'Developer Costs',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'13.6',description:'Furniture, Fittings & Equipment (Non-Construction)',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'13.7',description:'Artwork',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'13.8',description:'Loose IT Equipment',unit:'item',level:2,cost_type:'indirect',parent_code:'13'},
      {code:'14',description:'Risk Allowances',unit:'',level:1,cost_type:'indirect',parent_code:''},
      {code:'14.1',description:'Design Development Risks',unit:'%',level:2,cost_type:'indirect',parent_code:'14'},
      {code:'14.2',description:'Construction Risks',unit:'%',level:2,cost_type:'indirect',parent_code:'14'},
      {code:'14.3',description:'Employer Change Risks',unit:'%',level:2,cost_type:'indirect',parent_code:'14'},
      {code:'14.4',description:'Employer Other Risks',unit:'%',level:2,cost_type:'indirect',parent_code:'14'},
      {code:'15',description:'Inflation / Escalation',unit:'',level:1,cost_type:'indirect',parent_code:''},
      {code:'15.1',description:'Tender Inflation',unit:'%',level:2,cost_type:'indirect',parent_code:'15'},
      {code:'15.2',description:'Construction Inflation',unit:'%',level:2,cost_type:'indirect',parent_code:'15'},
      {code:'16',description:'Taxes',unit:'',level:1,cost_type:'indirect',parent_code:''},
      {code:'16.1',description:'VAT Assessment',unit:'%',level:2,cost_type:'indirect',parent_code:'16'},
      {code:'16.2',description:'Other Taxes / Levies',unit:'item',level:2,cost_type:'indirect',parent_code:'16'},
    ]
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Cost Codes')
    XLSX.writeFile(wb, 'NRM1_CostCodes_Template.xlsx')
  }

  function handleFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploadError('')
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary' })
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
        if (rows.length === 0) { setUploadError('الملف فاضي'); return }
        setUploadRows(rows)
      } catch { setUploadError('خطأ في قراءة الملف') }
    }
    reader.readAsBinaryString(file)
  }

  async function confirmUpload() {
    setUploading(true)
    try {
      const parents = uploadRows
        .filter(r => parseInt(r.level) === 1 || !r.parent_code)
        .filter(r => String(r.code||'').trim() && String(r.description||'').trim())
        .map(r => ({
          code: String(r.code).trim(),
          description: String(r.description).trim(),
          unit: r.unit ? String(r.unit).trim() : null,
          parent_id: null, level: 1,
          cost_type: ['direct','indirect'].includes(r.cost_type) ? r.cost_type : 'direct',
          is_active: true
        }))

      // Errors from every batch across all three stages (Level-1, Resource
      // Codes, Sub Codes) are collected here instead of only going to
      // console.error — a failed batch used to be silently swallowed:
      // the final success message counted rows ATTEMPTED (array.length),
      // not rows actually saved, so a real database error (RLS,
      // constraint violation, missing column, etc.) could report "✅
      // uploaded" while nothing was written. uploadErrors now drives both
      // the final message and the counts, so a partial or total failure
      // is always visible to the user, not just in the browser console.
      const uploadErrors = []

      let parentsSaved = 0
      for (let i = 0; i < parents.length; i += 50) {
        const batch = parents.slice(i,i+50).map(r=>({...r,project_id:projectId||null}))
        // onConflict must exactly match a real UNIQUE CONSTRAINT (not just
        // an index) for PostgREST to accept the upsert — confirmed via
        // pg_constraint: cost_codes_code_no_parent_unique is
        // UNIQUE(project_id, code) (a full constraint, not partial —
        // WHERE parent_id IS NULL is NOT part of its definition, despite
        // the constraint's name). 'code' alone does not match this
        // constraint's column list, which is exactly why upsert was
        // failing with "no unique or exclusion constraint matching the
        // ON CONFLICT specification" — project_id was simply missing
        // from the match, not a partial-index limitation as first
        // suspected.
        const { error } = await supabase.from('cost_codes').upsert(batch, { onConflict: 'project_id,code' })
        if (error) uploadErrors.push(`Parent batch (${batch.map(r=>r.code).join(', ')}): ${error.message}`)
        else parentsSaved += batch.length
      }

      // codeMap is rebuilt after each stage so later stages can resolve parent_code
      // against codes created earlier in this same upload (Cost Codes, then Resource Codes)
      let codeMap = {}
      async function refreshCodeMap() {
        // Scoped to this project only — cost_codes is project-scoped by
        // design (the large majority of queries against it in this file
        // filter on project_id), and an unscoped select() here could
        // silently map a code to another project's row of the same name
        // during this same bulk upload, exactly the class of risk found
        // in the onConflict review below.
        const { data: fresh } = await supabase.from('cost_codes').select('id, code').eq('project_id', projectId)
        codeMap = {}
        ;(fresh||[]).forEach(c => { codeMap[c.code] = c.id })
      }
      await refreshCodeMap()

      // Level 4 = Resource Code rows, inserted BEFORE Sub Codes so that a Sub Code's
      // parent_code can point at a Resource Code (e.g. "SC-DEW-01") instead of only a Cost Code.
      // Each Resource Code row must reference an existing entry in resource_dictionary
      // (via resource_dict_code, falling back to the row's own `code`).
      const resourceRows = uploadRows
        .filter(r => parseInt(r.level) === 4 && r.parent_code)
        .filter(r => String(r.code||r.resource_dict_code||'').trim())

      let resourceInserted = 0
      let resourceSkipped = []
      if (resourceRows.length > 0) {
        let dictQuery = supabase.from('resource_dictionary').select('*')
        if (projectId) dictQuery = dictQuery.eq('project_id', projectId)
        const { data: dictData } = await dictQuery
        const dictMap = {}
        ;(dictData || []).forEach(d => { dictMap[d.code] = d })

        const toInsert = []
        for (const r of resourceRows) {
          const dictCode = String(r.resource_dict_code || r.code).trim()
          const dictItem = dictMap[dictCode]
          const parentId = codeMap[String(r.parent_code).trim()]
          if (!dictItem || !parentId) { resourceSkipped.push(dictCode); continue }
          toInsert.push({
            code: dictItem.code,
            description: dictItem.description,
            unit: dictItem.unit || null,
            parent_id: parentId,
            level: 4,
            cost_type: ['direct','indirect'].includes(r.cost_type) ? r.cost_type : (codes.find(c=>c.id===parentId)?.cost_type || 'direct'),
            is_active: true,
            resource_dict_code: dictItem.code,
            project_id: projectId || null,
          })
        }
        for (let i = 0; i < toInsert.length; i += 50) {
          // onConflict must exactly match cost_codes_code_parent_unique =
          // UNIQUE(project_id, code, parent_id) — confirmed via
          // pg_constraint. Previously used 'code,parent_id' (missing
          // project_id) which — regardless of whether parent_id being a
          // uuid made cross-project collisions practically impossible —
          // still didn't match the constraint's exact column list, so
          // PostgREST rejected the upsert outright with "no unique or
          // exclusion constraint matching the ON CONFLICT specification".
          // toInsert rows already carry project_id (set below), so this
          // is a pure column-list correction, not a new safety concern.
          const batch = toInsert.slice(i,i+50)
          const { error } = await supabase.from('cost_codes').upsert(batch, { onConflict: 'project_id,code,parent_id' })
          if (error) uploadErrors.push(`Resource batch (${batch.map(r=>r.code).join(', ')}): ${error.message}`)
          else resourceInserted += batch.length
        }
      }

      // Refresh again so Sub Codes (level 2) can resolve parent_code against
      // the Resource Codes we just inserted, not just the original Cost Codes.
      await refreshCodeMap()

      const children = uploadRows
        .filter(r => parseInt(r.level) === 2 && r.parent_code)
        .filter(r => String(r.code||'').trim() && String(r.description||'').trim())
        .map(r => ({
          code: String(r.code).trim(),
          description: String(r.description).trim(),
          unit: r.unit ? String(r.unit).trim() : null,
          // parent_code may point at a Cost Code (level 1) OR a Resource Code (level 4) — both live in codeMap now
          parent_id: codeMap[String(r.parent_code).trim()] || null,
          level: 2,
          cost_type: ['direct','indirect'].includes(r.cost_type) ? r.cost_type : 'direct',
          is_active: true
        }))

      let childrenSaved = 0
      for (let i = 0; i < children.length; i += 50) {
        // onConflict must exactly match cost_codes_code_parent_unique =
        // UNIQUE(project_id, code, parent_id) — same fix as the Resource
        // Code batch above: 'code,parent_id' was missing project_id and
        // did not match the constraint's column list at all, which is
        // what caused the upsert to fail outright, not any partial-index
        // limitation.
        const batch = children.slice(i,i+50).map(r=>({...r,project_id:projectId||null}))
        const { error } = await supabase.from('cost_codes').upsert(batch, { onConflict: 'project_id,code,parent_id' })
        if (error) uploadErrors.push(`Child batch (${batch.map(r=>r.code).join(', ')}): ${error.message}`)
        else childrenSaved += batch.length
      }

      let msg
      if (uploadErrors.length > 0) {
        msg = `⚠️ فشل رفع بعض الصفوف — ${parentsSaved + resourceInserted + childrenSaved} كود اتحفظ بنجاح فقط من أصل ${parents.length + resourceRows.length + children.length}:\n\n`
        msg += uploadErrors.join('\n')
      } else {
        msg = `✅ تم رفع ${parentsSaved + resourceInserted + childrenSaved} كود (${parentsSaved} رئيسي + ${resourceInserted} resource + ${childrenSaved} فرعي)`
      }
      if (resourceSkipped.length > 0) {
        msg += `\n\n⚠️ تم تجاهل ${resourceSkipped.length} صف Resource لأن الكود مش موجود في الـ Resource Dictionary أو الـ Cost Code الأب مش موجود:\n${resourceSkipped.join(', ')}`
      }
      alert(msg)
      setUploadRows([]); setShowUpload(false)
      if (fileRef.current) fileRef.current.value = ''
      await fetchCodes()
    } catch(err) {
      alert('Error: ' + err.message)
    } finally {
      setUploading(false)
    }
  }

  function exportToExcel() {
    const rows = codes.map(c => ({
      'Code': c.code,
      'Description': c.description,
      'Unit': c.unit,
      'Level': c.level,
      'Type': c.cost_type,
      'Parent Code': codes.find(p => p.id === c.parent_id)?.code || '',
      'Active': c.is_active ? 'Yes' : 'No'
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Cost Codes')
    XLSX.writeFile(wb, 'cost_codes.xlsx')
  }

  // Build display tree
  const q = searchQuery.toLowerCase().trim()
  const matchCode = c => !q || (c.code||'').toLowerCase().includes(q) || (c.description||'').toLowerCase().includes(q)

  const parents = codes.filter(c => parseInt(c.level) === 1 && (filterType === 'all' || c.cost_type === filterType))
  // Orphans = Resource Codes (level 4) with no Cost Code parent at all (shouldn't normally happen, but guard against it)
  const orphans = codes.filter(c => parseInt(c.level) === 4 && !c.parent_id && (filterType === 'all' || c.cost_type === filterType) && matchCode(c))
  // Fully-orphaned Sub Codes = level 2 with parent_id NULL entirely (not even attached to a Cost Code).
  // Different from "orphanSubs" inside renderGroup, which are Sub Codes attached to a Cost Code but
  // not yet linked to a Resource Code — those already show a warning. These have NO parent at all,
  // so they never match any parent.id anywhere and previously vanished from the tree silently.
  const trulyOrphanSubs = codes.filter(c => parseInt(c.level) === 2 && !c.parent_id && (filterType === 'all' || c.cost_type === filterType) && matchCode(c))

  // When searching: show parents that match OR have matching children — including Sub Codes
  // nested one level deeper under a Resource Code (grandchildren), since that's the normal
  // structure now (Cost Code → Resource Code → Sub Code). Checking direct children only
  // missed a search hit on a Sub Code whenever it sat under a Resource Code.
  const hasMatchingDescendant = (p) => {
    const directChildren = codes.filter(c => c.parent_id === p.id)
    if (directChildren.some(matchCode)) return true
    // Resource Codes (level 4) directly under p — check their Sub Codes too
    const resourceIds = directChildren.filter(c => parseInt(c.level) === 4).map(c => c.id)
    if (resourceIds.length === 0) return false
    return codes.some(c => resourceIds.includes(c.parent_id) && matchCode(c))
  }
  const directParents = parents.filter(p => {
    if (!q) return p.cost_type === 'direct'
    return p.cost_type === 'direct' && (matchCode(p) || hasMatchingDescendant(p))
  })
  const indirectParents = parents.filter(p => {
    if (!q) return p.cost_type === 'indirect'
    return p.cost_type === 'indirect' && (matchCode(p) || hasMatchingDescendant(p))
  })

  const stats = {
    total: codes.length,
    parents: codes.filter(c => parseInt(c.level) === 1).length,
    resources: codes.filter(c => parseInt(c.level) === 4).length,
    subs: codes.filter(c => parseInt(c.level) === 2 && codes.some(p => p.id === c.parent_id && parseInt(p.level) === 4)).length,
    direct: codes.filter(c => c.cost_type === 'direct').length,
    indirect: codes.filter(c => c.cost_type === 'indirect').length,
  }

  function renderGroup(groupCodes, groupLabel, color) {
    if (groupCodes.length === 0) return null
    return (
      <div key={groupLabel} className="mb-4">
        <div className={`px-4 py-2 rounded-lg mb-2 text-sm font-bold ${color}`}>
          {groupLabel}
        </div>
        {groupCodes.map(parent => {
          // Resource Codes (level 4) directly under this Cost Code
          const resourceChildren = codes.filter(c => c.parent_id === parent.id && parseInt(c.level) === 4)
          // Orphan Sub Codes (level 2, legacy) still sitting directly under the Cost Code — not yet linked to a Resource Code
          const orphanSubs = codes.filter(c => c.parent_id === parent.id && parseInt(c.level) === 2)
          const filteredResources = q ? resourceChildren.filter(r => matchCode(r) || codes.some(sc => sc.parent_id === r.id && matchCode(sc))) : resourceChildren
          const isExpanded = q ? true : expandedParents.has(parent.id)

          // Tender Match-Check — only meaningful for Direct Cost Codes (tender_direct_items is a Direct-only source)
          let matchStatus = null // null = n/a (indirect), 'match', 'mismatch', 'no-tender-data'
          let mismatchDetail = null
          if (parent.cost_type === 'direct') {
            const tenderSet = tenderResourceMap[norm(parent.code)]
            const treeSet = new Set(resourceChildren.map(r => norm(r.resource_dict_code || r.code)))
            if (!tenderSet) {
              matchStatus = treeSet.size === 0 ? null : 'no-tender-data' // no tender rows at all for this cost code
            } else {
              const missingInTree = [...tenderSet].filter(x => !treeSet.has(x))
              const missingInTender = [...treeSet].filter(x => !tenderSet.has(x))
              if (missingInTree.length === 0 && missingInTender.length === 0) {
                matchStatus = 'match'
              } else {
                matchStatus = 'mismatch'
                mismatchDetail = { missingInTree, missingInTender }
              }
            }
          }
          return (
            <div key={parent.id} className="mb-1">
              {/* Cost Code Row (Level 1) */}
              <div className={`flex items-center border rounded-lg px-3 py-2.5 ${isExpanded ? 'bg-[#F3F4F6] border-[#1A7A4A]/30' : 'bg-white border-[#D1D5DB]'} hover:bg-[#F3F4F6] transition-colors`}>
                <button onClick={() => toggleExpand(parent.id)}
                  className="mr-2 text-[#9CA3AF] hover:text-[#6B7280] w-5 text-center font-bold">
                  {(resourceChildren.length > 0 || orphanSubs.length > 0) ? (isExpanded ? '▼' : '▶') : '·'}
                </button>
                <span className="font-mono font-bold text-[#1A7A4A] min-w-[9rem] max-w-[14rem] shrink-0 pr-3 break-words">{parent.code}</span>
                <span className="font-semibold text-[#0F1017] flex-1">{parent.description}</span>
                <span className="text-xs text-[#9CA3AF] w-16 text-center">{parent.unit || '—'}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full mr-2 ${parent.cost_type === 'direct' ? 'bg-[#D1D5DB] text-[#1A7A4A]' : 'bg-teal-100 text-teal-700'}`}>
                  {parent.cost_type}
                </span>
                {parent.cost_type === 'indirect' && (
                  parent.indirect_category_id ? (
                    <span className="text-xs px-2 py-0.5 rounded-full mr-2 bg-purple-100 text-purple-700">
                      🏷 {categories.find(c => c.id === parent.indirect_category_id)?.name || '—'}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full mr-2 bg-gray-100 text-gray-400 italic">no category</span>
                  )
                )}
                {matchStatus === 'match' && (
                  <span className="text-xs px-2 py-0.5 rounded-full mr-2 bg-green-100 text-green-700" title="متطابق تماماً مع Resource Codes الموجودة في التندر">
                    ✓ Synced with Tender
                  </span>
                )}
                {matchStatus === 'mismatch' && (
                  <button onClick={() => setShowMismatchDetail({ costCode: parent.code, description: parent.description, ...mismatchDetail })}
                    className="text-xs px-2 py-0.5 rounded-full mr-2 bg-red-100 text-red-700 hover:bg-red-200" title="اضغط لمعرفة التفاصيل">
                    ⚠ Mismatch with Tender
                  </button>
                )}
                {matchStatus === 'no-tender-data' && (
                  <span className="text-xs px-2 py-0.5 rounded-full mr-2 bg-gray-100 text-gray-400 italic" title="لا يوجد بيانات لهذا الكوست كود في التندر">
                    no tender data
                  </span>
                )}
                <span className="text-xs text-[#9CA3AF] mr-3">{resourceChildren.length} resources</span>
                <div className="flex items-center gap-1">
                  {can('CostCodes.Edit') && (
                    <button onClick={() => openNew(parent.id)} className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded border border-green-200 hover:bg-green-50">+ Resource</button>
                  )}
                  {can('CostCodes.Edit') && (
                    <button onClick={() => openEdit(parent)} className="text-[#9CA3AF] hover:text-[#6B7280]">✏</button>
                  )}
                  {can('CostCodes.Edit') && (
                    <button onClick={() => deleteCode(parent.id)} disabled={deletingIds.has(parent.id)} className="text-[#9CA3AF] hover:text-red-500 disabled:opacity-40 disabled:cursor-wait">🗑</button>
                  )}
                </div>
              </div>

              {/* Resource Codes (level 4) */}
              {isExpanded && filteredResources.map(res => {
                const subCodes = codes.filter(c => c.parent_id === res.id && parseInt(c.level) === 2)
                const filteredSubs = q ? subCodes.filter(matchCode) : subCodes
                const resExpanded = q ? true : expandedResources.has(res.id)
                return (
                  <div key={res.id} className="ml-6 mt-0.5">
                    <div className={`flex items-center border rounded-lg px-3 py-2 ${q && matchCode(res) ? 'bg-yellow-50 border-yellow-300' : 'bg-blue-50 border-blue-200'}`}>
                      <button onClick={() => toggleExpandResource(res.id)}
                        className="mr-2 text-[#9CA3AF] hover:text-[#6B7280] w-5 text-center font-bold">
                        {subCodes.length > 0 ? (resExpanded ? '▼' : '▶') : '·'}
                      </button>
                      <span className="mr-1 text-gray-300 text-xs">└</span>
                      <span className="font-mono font-semibold text-[#1A7A4A] min-w-[8rem] max-w-[12rem] shrink-0 text-sm pr-3 break-words">{res.code}</span>
                      <span className="text-[#2E2C29] flex-1 text-sm">{res.description}</span>
                      <span className="text-xs text-[#9CA3AF] w-16 text-center">{res.unit || '—'}</span>
                      <span className="text-xs text-[#9CA3AF] mr-3">{subCodes.length} subs</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setResourcePicker({ parentId: parent.id, attachToResourceId: res.id, reparentMode: true })}
                          className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50">↳ Link Sub</button>
                        <button onClick={() => toggleActive(res.id, res.is_active)} disabled={togglingIds.has(res.id)}
                          className={`text-xs px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-wait ${res.is_active ? 'text-green-600 border-green-200' : 'text-[#9CA3AF] border-[#D1D5DB]'}`}>
                          {res.is_active ? '✓' : '○'}
                        </button>
                        {can('CostCodes.Edit') && (
                        <button onClick={() => deleteCode(res.id)} disabled={deletingIds.has(res.id)} className="text-[#9CA3AF] hover:text-red-500 disabled:opacity-40 disabled:cursor-wait">🗑</button>
                      )}
                      </div>
                    </div>

                    {/* Level 3: Sub Codes (untouched free-text codes, just re-parented) */}
                    {resExpanded && filteredSubs.map(sub => (
                      <div key={sub.id} className={`flex items-center border rounded-lg px-3 py-2 ml-6 mt-0.5 ${q && matchCode(sub) ? 'bg-yellow-50 border-yellow-300' : sub.is_active ? 'bg-white border-[#E5E7EB]' : 'bg-[#F3F4F6] border-[#E5E7EB] opacity-60'}`}>
                        <span className="mr-2 text-gray-300 w-5 text-center">└</span>
                        <span className="font-mono text-[#6B7280] min-w-[8rem] max-w-[12rem] shrink-0 text-sm pr-3 break-words">{sub.code}</span>
                        <span className="text-[#2E2C29] flex-1 text-sm">{sub.description}</span>
                        <span className="text-xs text-[#9CA3AF] w-16 text-center">{sub.unit || '—'}</span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => toggleActive(sub.id, sub.is_active)} disabled={togglingIds.has(sub.id)}
                            className={`text-xs px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-wait ${sub.is_active ? 'text-green-600 border-green-200' : 'text-[#9CA3AF] border-[#D1D5DB]'}`}>
                            {sub.is_active ? '✓' : '○'}
                          </button>
                          {can('CostCodes.Edit') && (
                            <button onClick={() => openEdit(sub)} className="text-[#9CA3AF] hover:text-[#6B7280]">✏</button>
                          )}
                          {can('CostCodes.Edit') && (
                            <button onClick={() => deleteCode(sub.id)} disabled={deletingIds.has(sub.id)} className="text-[#9CA3AF] hover:text-red-500 disabled:opacity-40 disabled:cursor-wait">🗑</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })}

              {/* Orphan Sub Codes: still directly under the Cost Code, not yet linked to a Resource Code */}
              {isExpanded && orphanSubs.length > 0 && (
                <div className="ml-6 mt-1 mb-1 space-y-0.5">
                  <p className="text-xs text-yellow-700 font-medium mb-1 px-1">⚠ Sub Codes غير مربوطة بـ Resource Code:</p>
                  {(q ? orphanSubs.filter(matchCode) : orphanSubs).map(sub => (
                    <div key={sub.id} className={`flex items-center border rounded-lg px-3 py-2 ${q && matchCode(sub) ? 'bg-yellow-100 border-yellow-300' : 'bg-yellow-50 border-yellow-200'}`}>
                      <span className="font-mono text-[#6B7280] min-w-[8rem] max-w-[12rem] shrink-0 text-sm pr-3 break-words">{sub.code}</span>
                      <span className="text-[#2E2C29] flex-1 text-sm">{sub.description}</span>
                      <span className="text-xs text-[#9CA3AF] w-16 text-center">{sub.unit || '—'}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setResourcePicker({ parentId: parent.id, orphanSubId: sub.id, reparentMode: true })}
                          className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 whitespace-nowrap">
                          ↳ Link to Resource
                        </button>
                        <button onClick={() => toggleActive(sub.id, sub.is_active)} disabled={togglingIds.has(sub.id)}
                          className={`text-xs px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-wait ${sub.is_active ? 'text-green-600 border-green-200' : 'text-[#9CA3AF] border-[#D1D5DB]'}`}>
                          {sub.is_active ? '✓' : '○'}
                        </button>
                        {can('CostCodes.Edit') && (
                          <button onClick={() => openEdit(sub)} className="text-[#9CA3AF] hover:text-[#6B7280]">✏</button>
                        )}
                        {can('CostCodes.Edit') && (
                          <button onClick={() => deleteCode(sub.id)} disabled={deletingIds.has(sub.id)} className="text-[#9CA3AF] hover:text-red-500 disabled:opacity-40 disabled:cursor-wait">🗑</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1017]">🏷 Cost Codes</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">Cost Code → Resource Code → Sub Code</p>
        </div>
        {activeTab === 'tree' ? (
          <div className="flex gap-2">
            <button onClick={downloadTemplate} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F3F4F6]">📥 Template</button>
            <button onClick={downloadNRM1} className="px-3 py-2 text-sm border border-[#1A7A4A] text-[#1A7A4A] rounded-lg hover:bg-[#1A7A4A] hover:text-white transition-colors">📥 NRM1 Template</button>
            {can('CostCodes.Edit') && (
              <button onClick={() => setShowUpload(!showUpload)} className="px-3 py-2 text-sm border border-blue-300 text-[#1A7A4A] rounded-lg hover:bg-[#F3F4F6]">↑ Upload</button>
            )}
            <button onClick={exportToExcel} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F3F4F6]">↓ Export</button>
            <button onClick={() => setShowCategoryList(true)} className="px-3 py-2 text-sm border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50">🏷 Categories</button>
            {can('CostCodes.Edit') && (
              <button onClick={() => openNew()} className="px-4 py-2 text-sm bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637]">+ New Cost Code</button>
            )}
          </div>
        ) : (
          <div className="flex gap-2">
            {can('CostCodes.Edit') && (
              <button onClick={downloadDictTemplate} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F3F4F6]">📥 Template</button>
            )}
            {can('CostCodes.Edit') && (
              <button onClick={() => setShowDictUpload(!showDictUpload)} className="px-3 py-2 text-sm border border-blue-300 text-[#1A7A4A] rounded-lg hover:bg-[#F3F4F6]">↑ Upload</button>
            )}
            <button onClick={exportDictToExcel} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-[#F3F4F6]">↓ Export</button>
            {can('CostCodes.Edit') && (
              <button onClick={openDictNew} className="px-4 py-2 text-sm bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637]">+ New Resource Code</button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[#D1D5DB]">
        {[
          { id: 'tree', label: '🌳 Cost Codes' },
          { id: 'dictionary', label: '📖 Resource Dictionary' },
          { id: 'validation', label: '🛡 Validation' },
          { id: 'audit', label: '📜 سجل التغييرات' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-[#1A7A4A] text-[#1A7A4A]' : 'border-transparent text-[#6B7280] hover:text-[#2E2C29]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'tree' && (
      <>
      {/* Stats */}
      <div className="grid grid-cols-6 gap-3 mb-5">
        {[
          { label: 'Total', value: stats.total, color: 'text-[#2E2C29]' },
          { label: 'Cost Codes', value: stats.parents, color: 'text-[#1A7A4A]' },
          { label: 'Resource Codes', value: stats.resources, color: 'text-blue-600' },
          { label: 'Sub Codes', value: stats.subs, color: 'text-[#6B7280]' },
          { label: 'Direct', value: stats.direct, color: 'text-[#1A7A4A]' },
          { label: 'Indirect', value: stats.indirect, color: 'text-teal-600' },
        ].map(s => (
          <div key={s.label} className="bg-[#F3F4F6] rounded-xl border border-[#D1D5DB] p-3 text-center">
            <p className="text-xs text-[#6B7280]">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Upload */}
      {showUpload && (
        <div className="bg-[#F3F4F6] border border-[#1A7A4A]/30 rounded-xl p-5 mb-5">
          <h3 className="font-semibold text-blue-800 mb-2">رفع Cost Codes من Excel</h3>
          <p className="text-xs text-[#1A7A4A] mb-3">
            الأعمدة: <strong>code, description</strong> — الاختيارية: unit, level (1=Cost Code / 2=Sub Code / 4=Resource Code), cost_type (direct/indirect), parent_code, resource_dict_code
          </p>
          <p className="text-xs text-[#1A7A4A] mb-3">⚠ للـ level=4 (Resource Code): لازم الكود يكون موجود مسبقاً في Resource Dictionary، وparent_code يشاور على الـ Cost Code (level=1).</p>
          <p className="text-xs text-[#1A7A4A] mb-3">⚠ للـ level=2 (Sub Code): parent_code ينفع يشاور إما على Cost Code مباشرة أو على Resource Code (لو عايز الـ Sub Code يترتبط تحته من الرفع مباشرة). رتّب صفوف الـ Resource Code (level=4) قبل صفوف الـ Sub Code في نفس الملف.</p>
          <p className="text-xs text-[#1A7A4A] mb-3">⚠ الـ Parents يتم رفعهم أولاً تلقائياً ثم الـ Children.</p>
          <div className="flex items-center gap-3 mb-3">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload}
              className="text-sm text-[#6B7280] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1A7A4A] file:text-white file:text-sm" />
          </div>
          {uploadError && <p className="text-red-500 text-sm mb-2">{uploadError}</p>}
          {uploadRows.length > 0 && (
            <div>
              <p className="text-sm text-[#1A7A4A] mb-2">تم قراءة <strong>{uploadRows.length}</strong> كود</p>
              <div className="overflow-x-auto mb-3 max-h-36">
                <table className="text-xs w-full bg-[#F3F4F6] rounded border border-[#1A7A4A]/30">
                  <thead className="bg-[#D1D5DB]">
                    <tr>{Object.keys(uploadRows[0]).map(k => <th key={k} className="px-2 py-1 text-left font-medium text-[#1A7A4A]">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {uploadRows.slice(0, 4).map((row, i) => (
                      <tr key={i} className="border-t border-blue-100">
                        {Object.values(row).map((v, j) => <td key={j} className="px-2 py-1 text-[#6B7280]">{String(v).slice(0, 20)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {can('CostCodes.Edit') && (
                <button onClick={confirmUpload} disabled={uploading} className="px-4 py-2 bg-[#1A7A4A] text-white text-sm rounded-lg hover:bg-[#156637] disabled:opacity-50">
                  {uploading ? '⏳ جاري الرفع...' : `✓ تأكيد الرفع (${uploadRows.length} كود)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filter + Expand/Collapse */}
      <div className="flex flex-col gap-3 mb-4">
        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
          </svg>
          <input
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value)
              if (e.target.value.trim()) expandAll()
            }}
            placeholder="ابحث بـ كود أو وصف..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] text-lg leading-none">×</button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {['all', 'direct', 'indirect'].map(f => (
              <button key={f} onClick={() => setFilterType(f)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${filterType === f ? 'bg-[#1A7A4A] text-white border-blue-600' : 'bg-white text-[#6B7280] border-gray-300 hover:bg-[#F3F4F6]'}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={expandAll} className="text-xs text-[#6B7280] hover:text-[#2E2C29] px-2 py-1 border border-[#D1D5DB] rounded">▼ Expand All</button>
            <button onClick={collapseAll} className="text-xs text-[#6B7280] hover:text-[#2E2C29] px-2 py-1 border border-[#D1D5DB] rounded">▶ Collapse All</button>
          </div>
        </div>
      </div>

      {/* Tree */}
      {loading ? (
        <div className="text-center py-12 text-[#6B7280]">Loading...</div>
      ) : codes.length === 0 ? (
        <div className="text-center py-16 bg-[#F3F4F6] rounded-xl border border-[#D1D5DB]">
          <div className="text-5xl mb-3">🏷</div>
          <p className="text-[#6B7280]">لا يوجد cost codes. اضغط "+ New Cost Code" أو ارفع من Excel.</p>
        </div>
      ) : (
        <div>
          {renderGroup(filterType === 'all' ? directParents : (filterType === 'direct' ? directParents : []), '📋 Direct Works', 'bg-[#D1D5DB] text-blue-800')}
          {renderGroup(filterType === 'all' ? indirectParents : (filterType === 'indirect' ? indirectParents : []), '📦 Indirect Works', 'bg-teal-100 text-teal-800')}
          {orphans.length > 0 && renderGroup(orphans, '⚠ Unassigned Resource Codes', 'bg-yellow-100 text-yellow-800')}
          {trulyOrphanSubs.length > 0 && (
            <div className="mb-4">
              <div className="px-4 py-2 rounded-lg mb-2 text-sm font-bold bg-red-100 text-red-800">
                ⚠ Unlinked Sub Codes — لا يوجد Cost Code أب لها إطلاقاً (مش هتظهر في أي تاب أو تقرير حتى تُربط)
              </div>
              <div className="space-y-0.5">
                {trulyOrphanSubs.map(sub => (
                  <div key={sub.id} className="flex items-center border rounded-lg px-3 py-2 bg-red-50 border-red-200">
                    <span className="font-mono text-[#6B7280] min-w-[8rem] max-w-[12rem] shrink-0 text-sm pr-3 break-words">{sub.code}</span>
                    <span className="text-[#2E2C29] flex-1 text-sm">{sub.description}</span>
                    <span className="text-xs text-[#9CA3AF] w-16 text-center">{sub.unit || '—'}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setOrphanSubCostCodePicker({ subId: sub.id })}
                        className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 whitespace-nowrap">
                        ↳ Link to Cost Code
                      </button>
                      <button onClick={() => toggleActive(sub.id, sub.is_active)} disabled={togglingIds.has(sub.id)}
                        className={`text-xs px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-wait ${sub.is_active ? 'text-green-600 border-green-200' : 'text-[#9CA3AF] border-[#D1D5DB]'}`}>
                        {sub.is_active ? '✓' : '○'}
                      </button>
                      {can('CostCodes.Edit') && (
                        <button onClick={() => openEdit(sub)} className="text-[#9CA3AF] hover:text-[#6B7280]">✏</button>
                      )}
                      {can('CostCodes.Edit') && (
                        <button onClick={() => deleteCode(sub.id)} disabled={deletingIds.has(sub.id)} className="text-[#9CA3AF] hover:text-red-500 disabled:opacity-40 disabled:cursor-wait">🗑</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {activeTab === 'dictionary' && (
        <ResourceDictionaryTab
          resourceDict={resourceDict}
          dictLoading={dictLoading}
          dictSearch={dictSearch}
          setDictSearch={setDictSearch}
          dictFilterType={dictFilterType}
          setDictFilterType={setDictFilterType}
          showDictUpload={showDictUpload}
          dictUploadRows={dictUploadRows}
          dictUploading={dictUploading}
          dictUploadError={dictUploadError}
          dictFileRef={dictFileRef}
          handleDictFileUpload={handleDictFileUpload}
          confirmDictUpload={confirmDictUpload}
          openDictEdit={openDictEdit}
          deleteDictItem={deleteDictItem}
          toggleDictActive={toggleDictActive}
          can={can}
        />
      )}

      {activeTab === 'validation' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-[#0F1017]">Cost Structure Validation</h3>
              <p className="text-sm text-[#6B7280] mt-0.5">
                يفحص كل الموديولات (Purchase Orders, Commitments, Finance, Actual Cost, Budget, Forecast, IPC)
                ويطلع أي كود مستخدم في مكان في النظام لكن مش موجود صح في شجرة Cost Codes الحالية.
                فحص للقراءة فقط — مش هيعدل أي بيانات.
              </p>
            </div>
            <button onClick={runValidation} disabled={validationLoading}
              className="px-4 py-2 text-sm bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] disabled:opacity-50 disabled:cursor-wait whitespace-nowrap">
              {validationLoading ? 'جاري الفحص...' : '▶ Run Validation'}
            </button>
          </div>

          {validationError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
              حصل خطأ أثناء الفحص: {validationError}
            </div>
          )}

          {validationResults === null && !validationLoading && !validationError && (
            <div className="text-center py-16 bg-[#F3F4F6] rounded-xl border border-[#D1D5DB]">
              <div className="text-5xl mb-3">🛡</div>
              <p className="text-[#6B7280]">اضغط "Run Validation" لبدء الفحص.</p>
            </div>
          )}

          {validationLoading && (
            <div className="text-center py-16 text-[#6B7280]">جاري فحص كل الموديولات...</div>
          )}

          {validationResults !== null && !validationLoading && validationResults.length === 0 && (
            <div className="text-center py-16 bg-green-50 rounded-xl border border-green-200">
              <div className="text-5xl mb-3">✅</div>
              <p className="text-green-700 font-semibold text-lg">Cost Structure Validation Passed</p>
            </div>
          )}

          {validationResults !== null && !validationLoading && validationResults.length > 0 && (() => {
            const hardIssues = validationResults.filter(r => !r.issue.startsWith('Unclassified Commitment'))
            const unclassifiedCount = validationResults.length - hardIssues.length
            return (
            <div className="border border-red-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-red-100 text-red-800 text-sm font-bold">
                ⚠ {validationResults.length} مشكلة اتلاقت
                {hardIssues.length > 0 && ` — ${hardIssues.length} محتاجة تصحيح في الـ Master Data`}
                {unclassifiedCount > 0 && ` — ${unclassifiedCount} تحذير (Unclassified Commitment، مش خطأ بيانات لازم تصحيحه فورًا)`}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F3F4F6] text-left text-xs text-[#6B7280] uppercase">
                    <th className="px-4 py-2">Code</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2">Level</th>
                    <th className="px-4 py-2">Used In</th>
                    <th className="px-4 py-2">Issue</th>
                    <th className="px-4 py-2">Suggestion</th>
                  </tr>
                </thead>
                <tbody>
                  {validationResults.map((r, i) => {
                    const isUnclassified = r.issue.startsWith('Unclassified Commitment')
                    return (
                    <tr key={i} className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]">
                      <td className="px-4 py-2 font-mono text-[#6B7280]">{r.code}</td>
                      <td className="px-4 py-2 text-[#2E2C29]">{r.description}</td>
                      <td className="px-4 py-2 text-[#6B7280]">{r.level}</td>
                      <td className="px-4 py-2 text-[#6B7280]">{r.module}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isUnclassified ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {r.issue}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-blue-700">
                        {r.suggestion ? <>هل تقصد: <span className="font-mono font-semibold">{r.suggestion}</span>؟</> : '—'}
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            )
          })()}
        </div>
      )}

      {/* ── Audit Trail tab — read-only viewer of audit_trail rows this
          file writes (entity_type='cost_code', from logAudit() calls in
          reparentSubCode / reparentMultipleSubCodes / executeSaveCode's
          Cost Type change path). Loaded lazily on first visit to this
          tab, not on page load, since it's a secondary reference view. */}
      {activeTab === 'audit' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-[#0F1017]">سجل التغييرات (Audit Trail)</h3>
              <p className="text-sm text-[#6B7280] mt-0.5">
                يعرض كل عمليات إعادة التصنيف (Reparent) وتغيير Cost Type اللي حصلت على الأكواد في هذا المشروع — مين غيّر، إمتى، ومن إيه لإيه.
                للقراءة فقط.
              </p>
            </div>
            <button onClick={fetchAuditEntries} disabled={auditLoading}
              className="px-4 py-2 text-sm bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] disabled:opacity-50 disabled:cursor-wait whitespace-nowrap">
              {auditLoading ? 'جاري التحميل...' : (auditLoaded ? '↻ تحديث' : '▶ عرض السجل')}
            </button>
          </div>

          {auditError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
              حصل خطأ أثناء تحميل السجل: {auditError}
            </div>
          )}

          {!auditLoaded && !auditLoading && !auditError && (
            <div className="text-center py-16 bg-[#F3F4F6] rounded-xl border border-[#D1D5DB]">
              <div className="text-5xl mb-3">📜</div>
              <p className="text-[#6B7280]">اضغط "عرض السجل" لتحميل آخر التغييرات.</p>
            </div>
          )}

          {auditLoading && (
            <div className="text-center py-16 text-[#6B7280]">جاري تحميل السجل...</div>
          )}

          {auditLoaded && !auditLoading && auditEntries.length === 0 && (
            <div className="text-center py-16 bg-[#F3F4F6] rounded-xl border border-[#D1D5DB]">
              <div className="text-5xl mb-3">—</div>
              <p className="text-[#6B7280]">لا يوجد تغييرات مسجَّلة بعد.</p>
            </div>
          )}

          {auditLoaded && !auditLoading && auditEntries.length > 0 && (
            <div className="border border-[#D1D5DB] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F3F4F6] text-left text-xs text-[#6B7280] uppercase">
                    <th className="px-4 py-2">التاريخ</th>
                    <th className="px-4 py-2">العملية</th>
                    <th className="px-4 py-2">بواسطة</th>
                    <th className="px-4 py-2">التفاصيل</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEntries.map(e => {
                    const actionLabel = e.action === 'reparent' ? 'إعادة تصنيف (Reparent)'
                      : e.action === 'cost_type_change' ? 'تغيير Cost Type' : e.action
                    // Reparent stores before/after in `comment` (parent_id is a
                    // uuid, not a short status — see logAudit()'s own comment);
                    // Cost Type change uses previous_status/new_status directly.
                    const details = e.action === 'cost_type_change'
                      ? `${e.comment || '—'}: ${e.previous_status || '—'} → ${e.new_status || '—'}`
                      : (e.comment || '—')
                    return (
                      <tr key={e.id} className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]">
                        <td className="px-4 py-2 text-[#6B7280] whitespace-nowrap">
                          {e.created_at ? new Date(e.created_at).toLocaleString('en-GB') : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${e.action === 'reparent' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {actionLabel}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-[#2E2C29]">{e.actor_name || <span className="text-[#9CA3AF] italic">غير معروف</span>}</td>
                        <td className="px-4 py-2 text-[#6B7280] font-mono text-xs">{details}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal: New/Edit Cost Code (level 1) or Sub Code (level 3) */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
              <h2 className="text-lg font-bold">
                {editCode ? (parseInt(form.level) === 2 ? 'Edit Sub Code' : 'Edit Cost Code') : 'New Cost Code'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Level</label>
                  {/* Level 4 (Resource Code) is only created via the dictionary picker, not this form */}
                  <select value={form.level} disabled={parseInt(form.level) === 4}
                    onChange={e => setForm(p => ({ ...p, level: e.target.value, parent_id: e.target.value === '1' ? '' : p.parent_id }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                    <option value="1">1 — Cost Code</option>
                    <option value="2">2 — Sub Code</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Type</label>
                  <select value={form.cost_type} onChange={e => setForm(p => ({ ...p, cost_type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="direct">Direct</option>
                    <option value="indirect">Indirect</option>
                  </select>
                </div>
              </div>

              {parseInt(form.level) === 2 && (
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Parent (Cost Code or Resource Code)</label>
                  <select value={form.parent_id} onChange={e => setForm(p => ({ ...p, parent_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="">-- No Parent --</option>
                    {codes.filter(c => parseInt(c.level) === 1 || parseInt(c.level) === 4).map(c => (
                      <option key={c.id} value={c.id}>{c.code} — {c.description}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Code *</label>
                <input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
                  placeholder="31 23 19 أو S-C.Dew.1"
                  disabled={parseInt(form.level) === 4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Description *</label>
                <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Temporary Dewatering"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Unit</label>
                <input value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))}
                  placeholder="M2, TON, LS, Month..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="active" checked={form.is_active}
                  onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
                  className="rounded border-gray-300" />
                <label htmlFor="active" className="text-sm text-[#6B7280]">Active</label>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Cancel</button>
              {can('CostCodes.Edit') && (
                <button onClick={saveCode} disabled={renaming} className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] text-sm font-medium disabled:opacity-60">
                  {renaming ? 'جاري تحديث الصفحات المرتبطة...' : (editCode ? 'Update' : 'Create')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Resource Code Picker (choose from Resource Dictionary, attach to a Cost Code) */}
      {resourcePicker && (
        <ResourcePickerModal
          resourcePicker={resourcePicker}
          resourceDict={resourceDict}
          codes={codes}
          search={resourcePickerSearch}
          setSearch={setResourcePickerSearch}
          onClose={() => setResourcePicker(null)}
          onAttach={attachResourceCodes}
          onReparentToResource={reparentSubCode}
          onReparentMultiple={reparentMultipleSubCodes}
        />
      )}

      {/* Modal: Impact Analysis — Unified Operation Lifecycle's confirmation
          step for Reparent / Cost Type changes. Renders whenever
          pendingOperation is set, regardless of which entry point
          (single reparent, multi reparent, or Cost Type save) started it. */}
      {pendingOperation && (
        <ImpactAnalysisModal
          operation={pendingOperation.operation}
          codeLabel={pendingOperation.codeLabel}
          usage={pendingOperation.usage || { total: 0, modules: [] }}
          loading={pendingOperation.usageLoading}
          consistencyWarning={pendingOperation.consistencyWarning}
          onConfirm={confirmPendingOperation}
          onCancel={cancelPendingOperation}
        />
      )}

      {/* Modal: link a fully-orphaned Sub Code (parent_id NULL) directly to a Cost Code (level 1) */}
      {orphanSubCostCodePicker && (() => {
        const sub = codes.find(c => c.id === orphanSubCostCodePicker.subId)
        const q2 = orphanSubPickerSearch.toLowerCase().trim()
        const options = codes.filter(c => parseInt(c.level) === 1 &&
          (!q2 || (c.code||'').toLowerCase().includes(q2) || (c.description||'').toLowerCase().includes(q2)))
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
                <div>
                  <h2 className="text-lg font-bold">Link Sub Code to Cost Code</h2>
                  {sub && <p className="text-sm text-[#6B7280] mt-1">
                    <span className="font-mono font-bold text-[#1A7A4A]">{sub.code}</span> — {sub.description}
                  </p>}
                </div>
                <button onClick={() => { setOrphanSubCostCodePicker(null); setOrphanSubPickerSearch('') }} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
              </div>
              <div className="p-6 pb-3">
                <input autoFocus value={orphanSubPickerSearch} onChange={e => setOrphanSubPickerSearch(e.target.value)}
                  placeholder="ابحث عن Cost Code..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="px-6 pb-6 space-y-2 max-h-96 overflow-y-auto">
                {options.length === 0 ? (
                  <p className="text-sm text-[#6B7280]">لا يوجد نتائج.</p>
                ) : options.map(cc => (
                  <button key={cc.id} onClick={() => {
                      reparentSubCode(orphanSubCostCodePicker.subId, cc.id)
                      setOrphanSubCostCodePicker(null); setOrphanSubPickerSearch('')
                    }}
                    className="w-full text-left flex items-center border rounded-lg px-3 py-2 bg-white border-[#E5E7EB] hover:bg-[#F3F4F6]">
                    <span className="font-mono font-bold text-[#1A7A4A] min-w-[9rem] max-w-[14rem] shrink-0 text-sm pr-3 break-words">{cc.code}</span>
                    <span className="text-[#2E2C29] flex-1 text-sm">{cc.description}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
                <button onClick={() => { setOrphanSubCostCodePicker(null); setOrphanSubPickerSearch('') }} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Cancel</button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Modal: New/Edit Resource Dictionary item */}
      {showDictModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
              <h2 className="text-lg font-bold">{editDictItem ? 'Edit Resource Code' : 'New Resource Code'}</h2>
              <button onClick={() => setShowDictModal(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Resource Type</label>
                <select value={dictForm.resource_type} onChange={e => setDictForm(p => ({ ...p, resource_type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="material">Material</option>
                  <option value="labour">Labour</option>
                  <option value="subcontract">Subcontract</option>
                  <option value="equipment">Equipment</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Code *</label>
                <input value={dictForm.code} onChange={e => setDictForm(p => ({ ...p, code: e.target.value }))}
                  placeholder="SC-DEW-01"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Description *</label>
                <input value={dictForm.description} onChange={e => setDictForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Dewatering Pump Installation & Operation Subcontractor"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Typical Unit</label>
                <input value={dictForm.unit} onChange={e => setDictForm(p => ({ ...p, unit: e.target.value }))}
                  placeholder="Month, m³, Ton, LS..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="dict-active" checked={dictForm.is_active}
                  onChange={e => setDictForm(p => ({ ...p, is_active: e.target.checked }))}
                  className="rounded border-gray-300" />
                <label htmlFor="dict-active" className="text-sm text-[#6B7280]">Active</label>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
              <button onClick={() => setShowDictModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Cancel</button>
              {can('CostCodes.Edit') && (
                <button onClick={saveDictItem} className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] text-sm font-medium">
                  {editDictItem ? 'Update' : 'Create'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Categories List */}
      {showCategoryList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
              <h2 className="text-lg font-bold">Indirect Categories</h2>
              <button onClick={() => setShowCategoryList(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
            </div>
            <div className="p-6 space-y-2 max-h-96 overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-sm text-[#6B7280]">لا يوجد Categories بعد. لو مفيش أي Category، الـ Indirect Cost Codes كلها بتظهر مجمّعة في تاب واحد "Indirect" زي المعتاد.</p>
              ) : categories.map(cat => {
                const memberCount = codes.filter(c => c.indirect_category_id === cat.id).length
                return (
                  <div key={cat.id} className="flex items-center justify-between border rounded-lg px-3 py-2.5 bg-white border-[#E5E7EB]">
                    <div>
                      <span className="font-semibold text-sm text-[#0F1017]">{cat.name}</span>
                      <span className="text-xs text-[#9CA3AF] ml-2">({memberCount} cost code{memberCount!==1?'s':''})</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {can('CostCodes.Edit') && (
                        <button onClick={() => { setShowCategoryList(false); openEditCategory(cat) }}
                          className="text-xs text-[#9CA3AF] hover:text-[#6B7280] px-2 py-1">✏ Edit</button>
                      )}
                      {can('CostCodes.Edit') && (
                        <button onClick={() => deleteCategory(cat.id)}
                          className="text-xs text-[#9CA3AF] hover:text-red-500 px-2 py-1">🗑</button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
              <button onClick={() => setShowCategoryList(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Close</button>
              {can('CostCodes.Edit') && (
                <button onClick={() => { setShowCategoryList(false); openNewCategory() }}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium">+ New Category</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add/Edit Category — name it, then check which Indirect Cost Codes belong to it */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
              <h2 className="text-lg font-bold">{editCategory ? 'Edit Category' : 'New Category'}</h2>
              <button onClick={() => setShowCategoryModal(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Category Name *</label>
                <input value={categoryNameInput} onChange={e => setCategoryNameInput(e.target.value)}
                  placeholder="Equipment & Transportation"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-2">اختر الـ Cost Codes التابعة لهذا الـ Category</label>
                <div className="space-y-1.5 max-h-72 overflow-y-auto border border-[#E5E7EB] rounded-lg p-2 bg-white">
                  {codes.filter(c => parseInt(c.level) === 1 && c.cost_type === 'indirect').map(cc => {
                    const belongsElsewhere = cc.indirect_category_id && cc.indirect_category_id !== editCategory?.id
                    const checked = categorySelectedCC.has(cc.id)
                    return (
                      <label key={cc.id} className={`flex items-center gap-2 px-2 py-1.5 rounded ${belongsElsewhere ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-[#F3F4F6]'}`}>
                        <input type="checkbox" checked={checked} disabled={belongsElsewhere}
                          onChange={() => setCategorySelectedCC(prev => {
                            const next = new Set(prev)
                            next.has(cc.id) ? next.delete(cc.id) : next.add(cc.id)
                            return next
                          })}
                          className="rounded border-gray-300" />
                        <span className="font-mono text-xs text-[#1A7A4A]">{cc.code}</span>
                        <span className="text-sm text-[#374151] flex-1">{cc.description}</span>
                        {belongsElsewhere && <span className="text-[10px] text-[#9CA3AF] italic">
                          in {categories.find(c => c.id === cc.indirect_category_id)?.name}
                        </span>}
                      </label>
                    )
                  })}
                  {codes.filter(c => parseInt(c.level) === 1 && c.cost_type === 'indirect').length === 0 && (
                    <p className="text-xs text-[#9CA3AF] p-2">لا يوجد Indirect Cost Codes في الشجرة بعد.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
              <button onClick={() => setShowCategoryModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Cancel</button>
              {can('CostCodes.Edit') && (
                <button onClick={saveCategory} className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium">
                  {editCategory ? 'Update' : 'Create'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Tender Mismatch Detail — shown when clicking the ⚠ Mismatch badge */}
      {showMismatchDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
              <div>
                <h2 className="text-lg font-bold text-red-700">⚠ Mismatch with Tender</h2>
                <p className="text-sm text-[#6B7280] mt-1">
                  <span className="font-mono font-bold text-[#1A7A4A]">{showMismatchDetail.costCode}</span> — {showMismatchDetail.description}
                </p>
              </div>
              <button onClick={() => setShowMismatchDetail(null)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {showMismatchDetail.missingInTree.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-700 mb-2">
                    موجودة في التندر لكن مش موجودة في شجرة Cost Codes:
                  </p>
                  <div className="space-y-1">
                    {showMismatchDetail.missingInTree.map(code => (
                      <div key={code} className="font-mono text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-amber-800">{code}</div>
                    ))}
                  </div>
                  <p className="text-[10.5px] text-[#9CA3AF] mt-1.5">
                    يعني: البادجت بتاع الكود ده موجود في التندر، بس مش هيظهر في Cost Control لأنه مش مربوط في الشجرة.
                  </p>
                </div>
              )}
              {showMismatchDetail.missingInTender.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-blue-700 mb-2">
                    موجودة في شجرة Cost Codes لكن مش موجودة في التندر:
                  </p>
                  <div className="space-y-1">
                    {showMismatchDetail.missingInTender.map(code => (
                      <div key={code} className="font-mono text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1.5 text-blue-800">{code}</div>
                    ))}
                  </div>
                  <p className="text-[10.5px] text-[#9CA3AF] mt-1.5">
                    يعني: الكود ده مربوط في الشجرة بس مالوش بادجت في التندر — البادجت بتاعه هيفضل صفر.
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
              <button onClick={() => setShowMismatchDetail(null)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Resource Dictionary Tab — same basics as the Cost Codes tab:
// table view, manual add/edit, Excel upload/export, search + type filter
// ============================================================
function ResourceDictionaryTab({
  resourceDict, dictLoading, dictSearch, setDictSearch, dictFilterType, setDictFilterType,
  showDictUpload, dictUploadRows, dictUploading, dictUploadError, dictFileRef,
  handleDictFileUpload, confirmDictUpload, openDictEdit, deleteDictItem, toggleDictActive, can,
}) {
  const q = dictSearch.toLowerCase().trim()
  const matchDict = d => !q || (d.code||'').toLowerCase().includes(q) || (d.description||'').toLowerCase().includes(q)
  const filtered = resourceDict
    .filter(d => dictFilterType === 'all' || d.resource_type === dictFilterType)
    .filter(matchDict)

  const typeCounts = {
    material: resourceDict.filter(d => d.resource_type === 'material').length,
    labour: resourceDict.filter(d => d.resource_type === 'labour').length,
    subcontract: resourceDict.filter(d => d.resource_type === 'subcontract').length,
    equipment: resourceDict.filter(d => d.resource_type === 'equipment').length,
  }

  const typeColors = {
    material: 'bg-blue-100 text-blue-700',
    labour: 'bg-green-100 text-green-700',
    subcontract: 'bg-red-100 text-red-700',
    equipment: 'bg-amber-100 text-amber-700',
  }

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Total', value: resourceDict.length, color: 'text-[#2E2C29]' },
          { label: 'Material', value: typeCounts.material, color: 'text-blue-600' },
          { label: 'Labour', value: typeCounts.labour, color: 'text-green-600' },
          { label: 'Subcontract', value: typeCounts.subcontract, color: 'text-red-600' },
          { label: 'Equipment', value: typeCounts.equipment, color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="bg-[#F3F4F6] rounded-xl border border-[#D1D5DB] p-3 text-center">
            <p className="text-xs text-[#6B7280]">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Upload */}
      {showDictUpload && (
        <div className="bg-[#F3F4F6] border border-[#1A7A4A]/30 rounded-xl p-5 mb-5">
          <h3 className="font-semibold text-blue-800 mb-2">رفع Resource Dictionary من Excel</h3>
          <p className="text-xs text-[#1A7A4A] mb-3">
            الأعمدة: <strong>code, resource_type, description</strong> — الاختيارية: unit
          </p>
          <p className="text-xs text-[#1A7A4A] mb-3">resource_type لازم يكون واحد من: material, labour, subcontract, equipment</p>
          <div className="flex items-center gap-3 mb-3">
            <input ref={dictFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleDictFileUpload}
              className="text-sm text-[#6B7280] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1A7A4A] file:text-white file:text-sm" />
          </div>
          {dictUploadError && <p className="text-red-500 text-sm mb-2">{dictUploadError}</p>}
          {dictUploadRows.length > 0 && (
            <div>
              <p className="text-sm text-[#1A7A4A] mb-2">تم قراءة <strong>{dictUploadRows.length}</strong> كود</p>
              <div className="overflow-x-auto mb-3 max-h-36">
                <table className="text-xs w-full bg-[#F3F4F6] rounded border border-[#1A7A4A]/30">
                  <thead className="bg-[#D1D5DB]">
                    <tr>{Object.keys(dictUploadRows[0]).map(k => <th key={k} className="px-2 py-1 text-left font-medium text-[#1A7A4A]">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {dictUploadRows.slice(0, 4).map((row, i) => (
                      <tr key={i} className="border-t border-blue-100">
                        {Object.values(row).map((v, j) => <td key={j} className="px-2 py-1 text-[#6B7280]">{String(v).slice(0, 20)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {can('CostCodes.Edit') && (
                <button onClick={confirmDictUpload} disabled={dictUploading} className="px-4 py-2 bg-[#1A7A4A] text-white text-sm rounded-lg hover:bg-[#156637] disabled:opacity-50">
                  {dictUploading ? '⏳ جاري الرفع...' : `✓ تأكيد الرفع (${dictUploadRows.length} كود)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search + Type Filter */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
          </svg>
          <input
            value={dictSearch}
            onChange={e => setDictSearch(e.target.value)}
            placeholder="ابحث بـ كود أو وصف..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          />
          {dictSearch && (
            <button onClick={() => setDictSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] text-lg leading-none">×</button>
          )}
        </div>
        <div className="flex gap-2">
          {['all', 'material', 'labour', 'subcontract', 'equipment'].map(f => (
            <button key={f} onClick={() => setDictFilterType(f)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${dictFilterType === f ? 'bg-[#1A7A4A] text-white border-blue-600' : 'bg-white text-[#6B7280] border-gray-300 hover:bg-[#F3F4F6]'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {dictLoading ? (
        <div className="text-center py-12 text-[#6B7280]">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#F3F4F6] rounded-xl border border-[#D1D5DB]">
          <div className="text-5xl mb-3">📖</div>
          <p className="text-[#6B7280]">لا يوجد resource codes. اضغط "+ New Resource Code" أو ارفع من Excel.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {filtered.map(d => (
            <div key={d.id} className={`flex items-center border rounded-lg px-3 py-2.5 ${d.is_active ? 'bg-white border-[#E5E7EB]' : 'bg-[#F3F4F6] border-[#E5E7EB] opacity-60'}`}>
              <span className="font-mono font-bold text-[#1A7A4A] min-w-[9rem] max-w-[14rem] shrink-0 pr-3 break-words">{d.code}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full mr-3 ${typeColors[d.resource_type] || 'bg-gray-100 text-gray-700'}`}>
                {d.resource_type}
              </span>
              <span className="text-[#2E2C29] flex-1 text-sm">{d.description}</span>
              <span className="text-xs text-[#9CA3AF] w-16 text-center">{d.unit || '—'}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => toggleDictActive(d.id, d.is_active)}
                  className={`text-xs px-2 py-1 rounded border ${d.is_active ? 'text-green-600 border-green-200' : 'text-[#9CA3AF] border-[#D1D5DB]'}`}>
                  {d.is_active ? '✓' : '○'}
                </button>
                {can('CostCodes.Edit') && (
                  <button onClick={() => openDictEdit(d)} className="text-[#9CA3AF] hover:text-[#6B7280]">✏</button>
                )}
                {can('CostCodes.Edit') && (
                  <button onClick={() => deleteDictItem(d.id)} className="text-[#9CA3AF] hover:text-red-500">🗑</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ============================================================
// Impact Analysis Modal — UI consumer of getCodeUsage() AND
// computeConsistencyWarning(). These are two deliberately separate
// concerns rendered in the same modal, never merged into one message:
//   - usage (from getCodeUsage()): "how many records will be affected?"
//   - consistencyWarning (from computeConsistencyWarning()): "will this
//     leave the tree in a Parent/Child cost_type-mismatched state?"
// getCodeUsage() returns { total, modules: [{ key, count, amount? }] }
// with no labels or wording of its own, and consistencyWarning is either
// null, a single { parentCode, parentDescription, parentCostType,
// resultingCostType } object (single reparent / Cost Type change), or an
// array of { code, warning } (bulk reparent) — this component decides
// how to present all of it. Pure presentation + a Confirm/Cancel
// decision — no data fetching of its own; the caller
// (reparentSubCode/reparentMultipleSubCodes/saveCode's Cost Type change
// path) is responsible for calling both functions and passing the
// results in.
// ============================================================
function ImpactAnalysisModal({ operation, codeLabel, usage, loading, consistencyWarning, onConfirm, onCancel }) {
  const operationLabel = operation === 'reparent' ? 'إعادة التصنيف (Reparent)' : 'تغيير Cost Type'
  const warningList = Array.isArray(consistencyWarning) ? consistencyWarning : (consistencyWarning ? [{ code: codeLabel, warning: consistencyWarning }] : [])
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
          <div>
            <h2 className="text-lg font-bold">تأكيد {operationLabel}</h2>
            <p className="text-sm text-[#6B7280] mt-1 font-mono">{codeLabel}</p>
          </div>
          <button onClick={onCancel} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
        </div>
        <div className="p-6">
          {/* Consistency Warning — separate section, separate color
              (red, vs amber for Impact below), rendered above Impact so
              the structural concern is seen first. Never blocks
              Confirm — informational only, per the agreed design. */}
          {warningList.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-900 mb-1">
                ⚠ تحذير اتساق (Consistency Warning)
              </p>
              <div className="space-y-1">
                {warningList.map((w, i) => (
                  <p key={i} className="text-xs text-red-800">
                    {warningList.length > 1 && <span className="font-mono font-semibold">{w.code}</span>}
                    {warningList.length > 1 && ': '}
                    سيصبح "{w.warning.resultingCostType}"، لكن أبوه ({w.warning.parentCode} — {w.warning.parentDescription}) لا يزال "{w.warning.parentCostType}".
                  </p>
                ))}
              </div>
              <p className="text-xs text-red-700 mt-1.5">
                هذا لن يمنع العملية، لكن الشجرة ستُظهر تعارضًا سيظهر في تاب Validation لاحقًا.
              </p>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-[#6B7280]">
              جارِ فحص السجلات المرتبطة...
            </div>
          ) : usage.total === 0 ? (
            <p className="text-sm text-[#2E2C29]">
              لا توجد سجلات مالية أو تشغيلية مرتبطة بهذا الكود حاليًا. يمكن المتابعة بأمان.
            </p>
          ) : (
            <>
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-900">
                  ⚠ هذا الكود مرتبط بـ {usage.total} سجل عبر {usage.modules.length} وحدة/وحدات.
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  المتابعة ستُعيد تصنيف هذه السجلات فورًا في كل التقارير التي تقرأ الشجرة الحالية.
                </p>
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {usage.modules.map(m => (
                  <div key={m.key} className="flex items-center justify-between text-sm px-3 py-2 bg-[#F5F1EA] rounded-lg">
                    <span className="text-[#2E2C29]">{m.key}</span>
                    <span className="font-mono font-bold text-[#1A7A4A]">{m.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
          <button onClick={onCancel} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">
            إلغاء
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
            متابعة {operationLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Resource Picker Modal — choose a Resource Code from the Dictionary
// and attach it under a Cost Code, OR re-parent an existing Sub Code
// to sit under a chosen/newly-attached Resource Code.
// ============================================================
function ResourcePickerModal({ resourcePicker, resourceDict, codes, search, setSearch, onClose, onAttach, onReparentToResource, onReparentMultiple }) {
  const parent = codes.find(c => c.id === resourcePicker.parentId)
  const q = search.toLowerCase().trim()
  const filtered = resourceDict.filter(d => d.is_active !== false && (!q || (d.code||'').toLowerCase().includes(q) || (d.description||'').toLowerCase().includes(q)))
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectedSubIds, setSelectedSubIds] = useState(new Set())

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleSelectSub(id) {
    setSelectedSubIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // Mode: linking an orphan/existing sub code to a resource code that's already attached
  if (resourcePicker.attachToResourceId) {
    const resourceCode = codes.find(c => c.id === resourcePicker.attachToResourceId)
    const availableSubs = codes.filter(c => c.parent_id === resourcePicker.parentId && parseInt(c.level) === 2)
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
            <div>
              <h2 className="text-lg font-bold">Link Sub Code</h2>
              <p className="text-sm text-[#6B7280] mt-1">
                إلى: <span className="font-mono font-bold text-[#1A7A4A]">{resourceCode?.code}</span>
                {resourceCode?.description && <span className="text-[#2E2C29]"> — {resourceCode.description}</span>}
              </p>
            </div>
            <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
          </div>
          <div className="p-6 space-y-2 max-h-96 overflow-y-auto">
            {availableSubs.length === 0 ? (
              <p className="text-sm text-[#6B7280]">لا يوجد Sub Codes أخرى تحت هذا الـ Cost Code لربطها.</p>
            ) : availableSubs.map(sub => {
              const checked = selectedSubIds.has(sub.id)
              return (
                <button key={sub.id} onClick={() => toggleSelectSub(sub.id)}
                  className={`w-full text-left flex items-center border rounded-lg px-3 py-2 transition-colors ${checked ? 'bg-blue-50 border-blue-300' : 'bg-white border-[#E5E7EB] hover:bg-[#F3F4F6]'}`}>
                  <input type="checkbox" checked={checked} onChange={() => {}} className="mr-3 rounded border-gray-300 shrink-0" />
                  <span className="font-mono text-[#6B7280] min-w-[8rem] max-w-[12rem] shrink-0 text-sm pr-3 break-words">{sub.code}</span>
                  <span className="text-[#2E2C29] flex-1 text-sm">{sub.description}</span>
                </button>
              )
            })}
          </div>
          <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
            <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Cancel</button>
            <button
              onClick={() => { onReparentMultiple(Array.from(selectedSubIds), resourcePicker.attachToResourceId); onClose() }}
              disabled={selectedSubIds.size === 0}
              className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
              ↳ ربط {selectedSubIds.size > 0 ? `(${selectedSubIds.size})` : ''}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Mode: linking an orphan sub code directly (from the yellow warning box) to a resource code
  if (resourcePicker.orphanSubId) {
    const resourceOptions = codes.filter(c => c.parent_id === resourcePicker.parentId && parseInt(c.level) === 4)
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
            <h2 className="text-lg font-bold">اختر Resource Code</h2>
            <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
          </div>
          <div className="p-6 space-y-2 max-h-96 overflow-y-auto">
            {resourceOptions.length === 0 ? (
              <p className="text-sm text-[#6B7280]">لا يوجد Resource Codes مضافة تحت هذا الـ Cost Code بعد. أضف واحد أولاً بزرار "+ Resource".</p>
            ) : resourceOptions.map(res => (
              <button key={res.id} onClick={() => { onReparentToResource(resourcePicker.orphanSubId, res.id); onClose() }}
                className="w-full text-left flex items-center border rounded-lg px-3 py-2 bg-white border-[#E5E7EB] hover:bg-[#F3F4F6]">
                <span className="font-mono font-bold text-[#1A7A4A] min-w-[8rem] max-w-[12rem] shrink-0 text-sm pr-3 break-words">{res.code}</span>
                <span className="text-[#2E2C29] flex-1 text-sm">{res.description}</span>
              </button>
            ))}
          </div>
          <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
            <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Close</button>
          </div>
        </div>
      </div>
    )
  }

  // Default mode: attach one or more Resource Codes (from the Dictionary) under this Cost Code
  const typeColors = {
    material: 'bg-blue-100 text-blue-700',
    labour: 'bg-green-100 text-green-700',
    subcontract: 'bg-red-100 text-red-700',
    equipment: 'bg-amber-100 text-amber-700',
  }
  const selectedItems = resourceDict.filter(d => selectedIds.has(d.id))
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-6 border-b border-[#D1D5DB]">
          <h2 className="text-lg font-bold">اختر Resource Code — تحت {parent?.code}</h2>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
        </div>
        <div className="p-6 pb-3">
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="ابحث في الـ Resource Dictionary..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          />
          {selectedIds.size > 0 && (
            <div className="mt-2">
              <p className="text-xs text-[#1A7A4A] font-medium mb-1.5">تم اختيار {selectedIds.size} كود (يفضل محفوظ حتى لو بحثت عن حاجة تانية):</p>
              <div className="flex flex-wrap gap-1.5">
                {resourceDict.filter(d => selectedIds.has(d.id)).map(d => (
                  <span key={d.id} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {d.code}
                    <button onClick={() => toggleSelect(d.id)} className="text-blue-600 hover:text-blue-900 font-bold">×</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 pb-6 space-y-2 max-h-96 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-[#6B7280]">لا يوجد نتائج. أضف كود جديد من تاب Resource Dictionary أولاً.</p>
          ) : filtered.map(d => {
            const checked = selectedIds.has(d.id)
            return (
              <button key={d.id} onClick={() => toggleSelect(d.id)}
                className={`w-full text-left flex items-center border rounded-lg px-3 py-2 transition-colors ${checked ? 'bg-blue-50 border-blue-300' : 'bg-white border-[#E5E7EB] hover:bg-[#F3F4F6]'}`}>
                <input type="checkbox" checked={checked} onChange={() => {}} className="mr-3 rounded border-gray-300 shrink-0" />
                <span className="font-mono font-bold text-[#1A7A4A] min-w-[8rem] max-w-[12rem] shrink-0 text-sm pr-3 break-words">{d.code}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full mr-2 shrink-0 ${typeColors[d.resource_type] || 'bg-gray-100 text-gray-700'}`}>
                  {d.resource_type}
                </span>
                <span className="text-[#2E2C29] flex-1 text-sm">{d.description}</span>
              </button>
            )
          })}
        </div>
        <div className="flex gap-3 p-6 border-t border-[#D1D5DB]">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F3F4F6] text-sm">Cancel</button>
          <button
            onClick={() => onAttach(resourcePicker.parentId, selectedItems)}
            disabled={selectedItems.length === 0}
            className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
            + إضافة {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}
